/**
 * Garage Admin View (Admin Only)
 * Tool management, maker onboarding, and agent publishing
 */

import React, { useState, useEffect } from 'react';
import { Plus, Users, Package, CheckCircle, XCircle, Eye, EyeOff, Edit2, Trash2, RefreshCw, Settings, Sparkles, MessageSquarePlus, ArrowUpDown, ArrowUp, ArrowDown, Building2, X, Search, ScrollText } from 'lucide-react';
import garageAPI from '../../../services/garage/api';
import MultiSelectDropdown from '../components/MultiSelectDropdown';
import GarageLoadingScreen from '../components/GarageLoadingScreen';
import GarageConfigManagement from '../components/GarageConfigManagement';
import GarageRoiConfigTab from '../components/GarageRoiConfigTab';
import GarageFeedbacksAdminPanel from '../components/GarageFeedbacksAdminPanel';
import GarageDepartmentsAdminPanel from '../components/GarageDepartmentsAdminPanel';
import GarageAdminActivityPanel from '../components/GarageAdminActivityPanel';
import BotIntegrationForm from '../components/BotIntegrationForm';
import ConfirmModal from '../modals/ConfirmModal';
import AlertModal from '../modals/AlertModal';
import { parseStepsText, formatStepsForForm } from '../../../utils/toolAppSubtype';
import { formatApiError } from '../../../utils/formatApiError';
import ImageUrlOrUpload from '../components/ImageUrlOrUpload';
import TourGuide from '../components/TourGuide';
import { ADMIN_TOUR_STEPS } from '../tours/adminTour';
import config from '../../../config/environment';
import {
  ROLE_OPTIONS,
  JOURNEY_OPTIONS,
  PERSONA_OPTIONS,
  SALES_STAGE_OPTIONS,
  TAG_OPTIONS,
  INTEGRATION_OPTIONS,
  ENVIRONMENT_OPTIONS,
  MODEL_OPTIONS,
  CAPABILITY_OPTIONS,
  ACCESS_ROLE_OPTIONS,
  ACCESS_ROLE_LABELS
} from '../constants/formOptions';
import {
  TEAM_OPTIONS as ONBOARDING_TEAM_OPTIONS,
  ROLE_OPTIONS as ONBOARDING_JOB_ROLE_OPTIONS,
} from '../constants/onboardingOptions';
import {
  canManageUsers,
  getAdminPersonaScopes,
  isPersonaInAdminScope,
  isDomainAdmin,
} from '../constants/roleAccess';
import {
  emitGovernanceTelemetry,
  governanceFailureReason,
  governanceGuardrailReason,
} from '../utils/governanceTelemetry';

const flattenOnboardingJobRoles = (rolesByCategory = ONBOARDING_JOB_ROLE_OPTIONS) => [
  ...(rolesByCategory.leadership || []),
  ...(rolesByCategory.operations || []),
];

const USER_COLUMN_STORAGE_KEY = 'ai-garage-admin-user-columns';
const USER_COLUMN_OPTIONS = [
  { key: 'role', label: 'Access Role' },
  { key: 'persona', label: 'Access Persona' },
  { key: 'workday', label: 'Workday Business Unit' },
  { key: 'activity', label: 'Last Activity' },
  { key: 'organization', label: 'Organization' },
  { key: 'status', label: 'Active User / Status' },
];
const DEFAULT_USER_COLUMNS = ['role', 'persona', 'status'];

const loadUserColumns = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(USER_COLUMN_STORAGE_KEY) || 'null');
    const allowed = new Set(USER_COLUMN_OPTIONS.map((column) => column.key));
    if (Array.isArray(stored)) {
      return stored.filter((column) => allowed.has(column));
    }
  } catch (error) {
    console.warn('Could not load saved user columns:', error);
  }
  return DEFAULT_USER_COLUMNS;
};

const parseAudienceEmails = (value = '') => Array.from(new Set(
  String(value)
    .split(/[\s,;]+/)
    .map((email) => email.trim().toLowerCase())
    .filter(Boolean)
));

const isValidAudienceEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const emptyBotIntegration = () => ({
  configured: false,
  credentialAvailable: false,
  credentialStatus: 'not_configured',
  credentialVersion: 0,
  botExtensionId: '',
  botEmail: '',
  serverUrl: 'https://platform.ringcentral.com',
  webUrlTemplate: 'https://app.ringcentral.com/l/messages/{conversationId}',
  rcAppUrlTemplate: '',
  fallbackUrl: '',
  enabled: true,
  webLaunchEnabled: true,
  rcAppLaunchEnabled: false,
  provideCredentials: false,
  credentials: {},
});

/** Resolve onboarding team/job-role id (or raw label) to a display label. */
const resolveOnboardingOptionLabel = (value, options) => {
  if (!value) return null;
  const raw = String(value).trim();
  if (!raw) return null;
  const match = (options || []).find(
    (opt) =>
      String(opt.id || opt.value || '').toLowerCase() === raw.toLowerCase() ||
      String(opt.label || '').toLowerCase() === raw.toLowerCase()
  );
  return match?.label || raw;
};

const GarageAdminView = ({
  userEmail,
  userRole,
  accessPersona,
  accessPersonas = [],
  canViewAdminActivity = false,
  onActivityAccessChange,
  onConfigChange,
  initialTab,
  initialRoiToolId = null,
  showOnboarding = false,
  onboardingChecked = true,
}) => {
  const departmentsTabEnabled = config.DEPARTMENTS_TAB_ENABLED;
  const adminPersonaScopes = getAdminPersonaScopes(
    userRole,
    accessPersonas,
    accessPersona
  );
  const adminPersonaScope = adminPersonaScopes[0] || null;
  const domainAdmin = isDomainAdmin(userRole);
  const mayManageUsers = canManageUsers(userRole);
  const canOpenActivity = userRole === 'admin' && canViewAdminActivity;
  const [activeTab, setActiveTab] = useState(
    (!mayManageUsers && ['users', 'departments'].includes(initialTab))
      || (initialTab === 'departments' && !departmentsTabEnabled)
      || (initialTab === 'activity' && !canOpenActivity)
      ? 'tools'
      : initialTab || 'tools'
  );
  const [tools, setTools] = useState([]);
  const [makers, setMakers] = useState([]);
  const [users, setUsers] = useState([]);
  const [audienceUsers, setAudienceUsers] = useState([]);
  const [audienceUsersLoading, setAudienceUsersLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isTestingBot, setIsTestingBot] = useState(false);
  const [isFetchingBotCredential, setIsFetchingBotCredential] = useState(false);

  // Dynamic options state - loaded from API
  const [dynamicOptions, setDynamicOptions] = useState({
    toolTargetRoles: [],
    journeys: [],
    personas: [],
    departments: [],
    salesStages: [],
    tags: [],
    integrations: [],
    environments: [],
    models: [],
    capabilities: [],
    onboardingTeams: ONBOARDING_TEAM_OPTIONS,
    onboardingJobRoles: flattenOnboardingJobRoles(),
  });
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [toolPersonaFilter, setToolPersonaFilter] = useState('');
  const [personaFilter, setPersonaFilter] = useState('');
  const [userRoleFilter, setUserRoleFilter] = useState('');
  const [visibleUserColumns, setVisibleUserColumns] = useState(loadUserColumns);
  const [userSort, setUserSort] = useState({ key: 'userEmail', dir: 'asc' });

  // Pagination and search state
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Tool form state
  const [showToolForm, setShowToolForm] = useState(false);
  const [editingTool, setEditingTool] = useState(null);
  const [toolFormData, setToolFormData] = useState({
    title: '',
    description: '',
    url: '',
    imageUrl: '',
    toolType: 'App',
    appSubtype: 'web',
    rcAppUrl: '',
    steps: '',
    status: 'draft',
    makerEmail: '',
    tags: [],           // Changed to array
    roles: [],          // Changed to array
    journeys: [],       // Changed to array
    capabilities: [],   // Added capabilities field
    persona: '',        // Primary persona/team (for badge display)
    // New enhanced fields
    environment: '',
    model: '',
    personas: [],       // Changed to array (for multi-select filtering)
    departments: [],
    audienceScope: 'departments',
    specificUserEmails: [],
    manualSpecificUserEmails: '',
    salesStages: [],    // Changed to array
    integrations: [],   // Changed to array
    whatItDoes: '',
    goodFitScenarios: '',
    notGoodFitScenarios: '',
    previewUrl: '',
    socialLinks: '',
    requireAccessRequest: false,
    botIntegration: emptyBotIntegration(),
  });

  // Session tracking config state (for Apps)
  const [sessionConfig, setSessionConfig] = useState({
    sourceTableName: '',
    sessionIdColumn: '',
    userEmailColumn: '',
    sessionStartColumn: '',
    sessionEndColumn: '',
    incrementalColumn: '',
    isActive: false
  });
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionTestResult, setConnectionTestResult] = useState(null);

  // Maker form state
  const [showMakerForm, setShowMakerForm] = useState(false);
  const [editingMaker, setEditingMaker] = useState(null);
  const [makerFormData, setMakerFormData] = useState({
    email: '',
    displayName: '',
    firstName: '',
    lastName: '',
    jobTitle: '',
    department: '',
    team: '',
    location: '',
    bio: '',
    profileImageUrl: '',
    slackHandle: '',
    focusAreas: ''
  });

  // User form state
  const [showUserForm, setShowUserForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userFormData, setUserFormData] = useState({
    userEmail: '',
    displayName: '',
    role: 'user',
    onboardingStatus: 'completed',
    department: '',
    workdayBusinessUnit: '',
    accessPersona: '',
    accessPersonas: [],
    grantedToolIds: [],
    canViewAdminActivity: false,
  });

  useEffect(() => {
    if (!showUserForm) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [showUserForm]);

  useEffect(() => {
    localStorage.setItem(
      USER_COLUMN_STORAGE_KEY,
      JSON.stringify(visibleUserColumns)
    );
  }, [visibleUserColumns]);

  // Modal states
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null,
    variant: 'default'
  });

  const [alertModal, setAlertModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    variant: 'info'
  });

  // Tour state — start after onboarding so they never overlap
  const [showTour, setShowTour] = useState(false);

  useEffect(() => {
    if (!onboardingChecked || showOnboarding) {
      setShowTour(false);
      return;
    }
    const tourDone = localStorage.getItem('tour-completed-admin') === 'true';
    setShowTour(!tourDone);
  }, [onboardingChecked, showOnboarding]);

  // Helper: Check if user can edit a specific tool
  // Full admin can edit all tools
  // Persona-admin restrictions:
  // - CAN edit tools whose primary persona matches their assigned scope
  // - CANNOT edit tools without a persona (null, empty, or whitespace)
  // - CANNOT edit tools with other personas (Customer Success, Product, etc.)
  const canEditTool = (tool) => {
    if (!userRole) return false;
    if (userRole === 'admin') return true; // Full admin can edit everything

    if (adminPersonaScopes.length) {
      if (!tool.persona || !tool.persona.trim()) {
        return false;
      }
      return isPersonaInAdminScope(
        userRole,
        tool.persona,
        adminPersonaScopes
      );
    }

    return false;
  };

  const toolActionTitle = (action, tool) => {
    if (canEditTool(tool) || !domainAdmin) return `${action} tool`;
    if (!tool.persona || !tool.persona.trim()) {
      return `Cannot ${action.toLowerCase()}: tool has no persona assigned (${adminPersonaScopes.join(', ')} Admin scope)`;
    }
    return `Cannot ${action.toLowerCase()}: tool persona is "${tool.persona}" (${adminPersonaScopes.join(', ')} Admin scope)`;
  };

  // Load dynamic options on component mount
  useEffect(() => {
    loadDynamicOptions();
  }, []);

  useEffect(() => {
    if (initialTab === 'makers') {
      setActiveTab('tools');
      return;
    }
    if (
      initialTab
      && ['tools', 'users', 'config', 'roi', 'feedbacks', 'departments'].includes(initialTab)
      && (initialTab !== 'departments' || departmentsTabEnabled)
      && (mayManageUsers || !['users', 'departments'].includes(initialTab))
    ) {
      setActiveTab(initialTab);
    }
  }, [departmentsTabEnabled, initialTab, mayManageUsers]);

  useEffect(() => {
    if (activeTab === 'makers') {
      setActiveTab('tools');
    } else if (!mayManageUsers && ['users', 'departments'].includes(activeTab)) {
      setActiveTab('tools');
    } else if (activeTab === 'activity' && !canOpenActivity) {
      setActiveTab('tools');
    }
  }, [activeTab, mayManageUsers, canOpenActivity]);

  useEffect(() => {
    if (activeTab === 'tools') {
      loadTools();
      // Reload dynamic options when switching back to tools tab
      // This ensures dropdowns are fresh after config changes
      loadDynamicOptions();
    } else if (activeTab === 'users' || activeTab === 'departments') {
      loadUsers();
      loadDynamicOptions();
    }
  }, [activeTab]);

  const loadDynamicOptions = async () => {
    setOptionsLoading(true);
    try {
      // Load all config options in parallel
      const [
        toolTargetRoles,
        journeys,
        personas,
        departments,
        salesStages,
        tags,
        integrations,
        environments,
        models,
        capabilities,
        teamOptions,
        jobRoleOptions,
      ] = await Promise.all([
        garageAPI.getConfigOptions('tool_target_role'),
        garageAPI.getConfigOptions('journey'),
        garageAPI.getConfigOptions('persona'),
        garageAPI.getConfigOptions('department'),
        garageAPI.getConfigOptions('sales_stage'),
        garageAPI.getConfigOptions('tag'),
        garageAPI.getConfigOptions('integration'),
        garageAPI.getConfigOptions('environment'),
        garageAPI.getConfigOptions('model'),
        garageAPI.getConfigOptions('capability'),
        garageAPI.getConfigOptions('team'),
        garageAPI.getConfigOptions('user_profile_role'),
      ]);

      const onboardingTeams = (teamOptions || []).length > 0
        ? teamOptions.map((t) => ({ id: t.value, label: t.label || t.value }))
        : ONBOARDING_TEAM_OPTIONS;

      const onboardingJobRoles = (jobRoleOptions || []).length > 0
        ? jobRoleOptions.map((r) => ({ id: r.value, label: r.label || r.value }))
        : flattenOnboardingJobRoles();

      setDynamicOptions({
        // Roles is the one category where value !== label (e.g. value "BDR",
        // label "BDR / SDR"). MultiSelectDropdown supports { value, label }
        // pairs so we can show the friendly label while still storing/matching
        // toolFormData.roles (and the tool's persisted roles array) by value.
        toolTargetRoles: toolTargetRoles.map(opt => ({ value: opt.value, label: opt.label || opt.value })),
        journeys: journeys.map(opt => opt.label),
        personas: personas.map(opt => opt.label),
        departments: (departments || []).map(opt => ({
          id: opt.id,
          value: opt.value,
          label: opt.label || opt.value,
        })),
        salesStages: salesStages.map(opt => opt.label),
        tags: tags.map(opt => opt.label),
        integrations: integrations.map(opt => opt.label),
        environments: environments.map(opt => opt.label),
        models: models.map(opt => opt.label),
        capabilities: capabilities.map(opt => opt.label),
        onboardingTeams,
        onboardingJobRoles,
      });
    } catch (error) {
      console.error('Failed to load dynamic options:', error);
      // Fallback to hardcoded options if API fails
      setDynamicOptions({
        toolTargetRoles: ROLE_OPTIONS,
        journeys: JOURNEY_OPTIONS,
        personas: PERSONA_OPTIONS,
        departments: [],
        salesStages: SALES_STAGE_OPTIONS,
        tags: TAG_OPTIONS,
        integrations: INTEGRATION_OPTIONS,
        environments: ENVIRONMENT_OPTIONS,
        models: MODEL_OPTIONS,
        capabilities: CAPABILITY_OPTIONS,
        onboardingTeams: ONBOARDING_TEAM_OPTIONS,
        onboardingJobRoles: flattenOnboardingJobRoles(),
      });
    } finally {
      setOptionsLoading(false);
    }
  };

  const loadTools = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      // Admin view: skip environment filter to show ALL tools (TEST, PRD, DEV, etc.)
      const data = await garageAPI.getTools({}, true);
      console.log('🔍 Admin View - Loaded tools:', data);
      console.log('🔍 Admin View - Total tools loaded (all environments):', data.length);
      // Log Legal Translator specifically for debugging
      const legalTool = data.find(t => t.title === 'Legal Translator');
      if (legalTool) {
        console.log('🔍 Legal Translator tool data:', {
          makerEmail: legalTool.makerEmail,
          makerName: legalTool.makerName,
          makers: legalTool.makers,
          createdBy: legalTool.createdBy,
          environment: legalTool.environment
        });
      }
      setTools(data);
    } catch (error) {
      console.error('Failed to load tools:', error);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const loadMakers = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const data = await garageAPI.getMakers();
      setMakers(data);
    } catch (error) {
      console.error('Failed to load makers:', error);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const loadUsers = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const data = await garageAPI.getUserProfiles();
      console.log('📊 Loaded user profiles:', data);
      console.log('📊 Total users found:', data?.length || 0);
      setUsers(data || []);
    } catch (error) {
      console.error('❌ Failed to load users:', error);
      console.error('Error details:', error.response?.data || error.message);
      setUsers([]);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const loadAudienceUsers = async (departments = toolFormData.departments) => {
    if (!departments?.length) {
      setAudienceUsers([]);
      return [];
    }
    setAudienceUsersLoading(true);
    try {
      const data = await garageAPI.getWorkdayAudienceUsers(departments);
      setAudienceUsers(data || []);
      return data || [];
    } catch (error) {
      console.error('Failed to load users for app audience', error);
      setAudienceUsers([]);
      return [];
    } finally {
      setAudienceUsersLoading(false);
    }
  };

  const addManualAudienceEmails = () => {
    const emails = parseAudienceEmails(toolFormData.manualSpecificUserEmails);
    const invalidEmails = emails.filter((email) => !isValidAudienceEmail(email));
    if (invalidEmails.length > 0) {
      setAlertModal({
        isOpen: true,
        title: 'Invalid email address',
        message: `Check these values and try again: ${invalidEmails.join(', ')}`,
        variant: 'error',
      });
      return;
    }
    setToolFormData((previous) => ({
      ...previous,
      specificUserEmails: Array.from(new Set([
        ...previous.specificUserEmails.map((email) => String(email).toLowerCase()),
        ...emails,
      ])),
      manualSpecificUserEmails: '',
    }));
  };

  const loadSessionConfig = async (toolId) => {
    try {
      const config = await garageAPI.getSessionConfigByTool(toolId);
      if (config) {
        setSessionConfig({
          sourceTableName: config.sourceTableName || '',
          sessionIdColumn: config.columnMapping?.session_id || '',
          userEmailColumn: config.columnMapping?.user_email || '',
          sessionStartColumn: config.columnMapping?.session_start || '',
          sessionEndColumn: config.columnMapping?.session_end || '',
          incrementalColumn: config.incrementalColumn || '',
          isActive: config.isActive || false
        });
      } else {
        // No config found - reset to empty state
        setSessionConfig({
          sourceTableName: '',
          sessionIdColumn: '',
          userEmailColumn: '',
          sessionStartColumn: '',
          sessionEndColumn: '',
          incrementalColumn: '',
          isActive: false
        });
      }
    } catch (error) {
      // Config doesn't exist or error occurred - reset to empty state
      console.log('No session config found for tool (this is normal for new tools)');
      setSessionConfig({
        sourceTableName: '',
        sessionIdColumn: '',
        userEmailColumn: '',
        sessionStartColumn: '',
        sessionEndColumn: '',
        incrementalColumn: '',
        isActive: false
      });
    }
  };

  const handleTestConnection = async () => {
    if (!sessionConfig.sourceTableName) {
      setAlertModal({
        isOpen: true,
        title: 'Missing Information',
        message: 'Please enter a source table name first.',
        variant: 'warning'
      });
      return;
    }

    setTestingConnection(true);
    setConnectionTestResult(null);

    try {
      const testData = {
        sourceType: 'postgres',
        sourceTableName: sessionConfig.sourceTableName,
        columnMapping: {
          session_id: sessionConfig.sessionIdColumn || 'id',
          user_email: sessionConfig.userEmailColumn || 'user_email',
          session_start: sessionConfig.sessionStartColumn || 'created_at',
          ...(sessionConfig.sessionEndColumn && { session_end: sessionConfig.sessionEndColumn })
        }
      };

      const result = await garageAPI.testSessionConnection(testData);
      setConnectionTestResult(result);

      if (result.success) {
        setAlertModal({
          isOpen: true,
          title: 'Connection Successful',
          message: `Successfully connected to table "${sessionConfig.sourceTableName}". Found ${result.rowCount || 0} recent sessions.`,
          variant: 'success'
        });
      }
    } catch (error) {
      console.error('Connection test failed:', error);
      setConnectionTestResult({
        success: false,
        message: 'Connection test failed',
        error: formatApiError(error, error.message || 'Connection failed')
      });
      setAlertModal({
        isOpen: true,
        title: 'Connection Failed',
        message: formatApiError(error, 'Failed to connect to the database table.'),
        variant: 'error'
      });
    } finally {
      setTestingConnection(false);
    }
  };

  const saveSessionConfig = async (toolId) => {
    try {
      // Only save session config if source table name is provided
      // Empty fields should remain empty, not use defaults
      if (!sessionConfig.sourceTableName || !sessionConfig.sourceTableName.trim()) {
        console.log('No source table name provided, skipping session config save');
        return;
      }

      // Build column mapping with only the values user actually entered
      const columnMapping = {};
      if (sessionConfig.sessionIdColumn) {
        columnMapping.session_id = sessionConfig.sessionIdColumn;
      }
      if (sessionConfig.userEmailColumn) {
        columnMapping.user_email = sessionConfig.userEmailColumn;
      }
      if (sessionConfig.sessionStartColumn) {
        columnMapping.session_start = sessionConfig.sessionStartColumn;
      }
      if (sessionConfig.sessionEndColumn) {
        columnMapping.session_end = sessionConfig.sessionEndColumn;
      }

      const configPayload = {
        toolId: toolId,
        sourceType: 'postgres',
        sourceTableName: sessionConfig.sourceTableName,
        columnMapping: columnMapping,
        incrementalColumn: sessionConfig.incrementalColumn,
        isActive: sessionConfig.isActive,
        syncFrequency: 'hourly',
        createdByEmail: userEmail
      };

      // Check if config exists first
      try {
        const existingConfig = await garageAPI.getSessionConfigByTool(toolId);
        if (existingConfig) {
          // Update existing config
          await garageAPI.updateSessionConfig(existingConfig.id, configPayload);
          console.log('Session config updated successfully');
        }
      } catch (error) {
        // Config doesn't exist, create new one
        await garageAPI.createSessionConfig(configPayload);
        console.log('Session config created successfully');
      }
    } catch (error) {
      console.error('Failed to save session config:', error);
      // Don't fail the whole tool save if session config fails
      setAlertModal({
        isOpen: true,
        title: 'Warning',
        message: `Tool saved but session config failed to save:\n${formatApiError(error, 'Unknown error')}\nYou can configure it later.`,
        variant: 'warning'
      });
    }
  };

  const handlePublishTool = async (toolId) => {
    try {
      await garageAPI.publishTool(toolId);
      loadTools({ silent: true });
      setAlertModal({
        isOpen: true,
        title: 'Success',
        message: 'Tool published successfully!',
        variant: 'success'
      });
    } catch (error) {
      console.error('Failed to publish tool:', error);
      setAlertModal({
        isOpen: true,
        title: 'Error',
        message: formatApiError(error, 'Failed to publish tool. Please try again.'),
        variant: 'error'
      });
    }
  };

  const handleUnpublishTool = async (toolId) => {
    try {
      await garageAPI.unpublishTool(toolId);
      loadTools({ silent: true });
      setAlertModal({
        isOpen: true,
        title: 'Success',
        message: 'Tool unpublished successfully!',
        variant: 'success'
      });
    } catch (error) {
      console.error('Failed to unpublish tool:', error);
      setAlertModal({
        isOpen: true,
        title: 'Error',
        message: formatApiError(error, 'Failed to unpublish tool. Please try again.'),
        variant: 'error'
      });
    }
  };

  // Tool Functions
  const handleEditTool = (tool) => {
    setEditingTool(tool);

    // Extract maker emails - now supporting multiple makers:
    // 1. If tool has makers array, extract all emails (comma-separated)
    // 2. Fall back to flat makerEmail field (backward compatibility)
    // 3. Fall back to createdByEmail
    let makerEmail = '';
    if (tool.makers && tool.makers.length > 0) {
      // Extract all maker emails and join with comma-space
      makerEmail = tool.makers.map(m => m.makerEmail).filter(e => e).join(', ');
    } else if (tool.makerEmail || tool.maker_email) {
      makerEmail = tool.makerEmail || tool.maker_email || '';
    } else {
      makerEmail = tool.createdByEmail || '';
    }

    // Primary persona: prefer scalar persona; fall back to first of personas[] for older rows
    // Match config options case-insensitively so the select shows the saved value
    let primaryPersona = (tool.persona || '').trim();
    if (!primaryPersona && Array.isArray(tool.personas) && tool.personas.length > 0) {
      primaryPersona = String(tool.personas[0] || '').trim();
    }
    if (primaryPersona && Array.isArray(dynamicOptions.personas) && dynamicOptions.personas.length > 0) {
      const matched = dynamicOptions.personas.find(
        (p) => String(p).trim().toLowerCase() === primaryPersona.toLowerCase()
      );
      if (matched) primaryPersona = matched;
    }

    setToolFormData({
      title: tool.title || '',
      description: tool.description || '',
      url: tool.url || '',
      imageUrl: tool.imageUrl || '',
      toolType: tool.toolType || 'App',
      appSubtype: tool.appSubtype || 'web',
      rcAppUrl: tool.rcAppUrl || '',
      steps: formatStepsForForm(tool.steps),
      status: tool.status || 'draft',
      makerEmail: makerEmail,
      tags: Array.isArray(tool.tags) ? tool.tags : [],
      roles: Array.isArray(tool.roles) ? tool.roles : [],
      journeys: Array.isArray(tool.journeys) ? tool.journeys : [],
      capabilities: Array.isArray(tool.capabilities) ? tool.capabilities : [],
      persona: primaryPersona,
      // New enhanced fields
      environment: tool.environment || '',
      model: tool.model || '',
      personas: Array.isArray(tool.personas) ? tool.personas : [],
      departments: Array.isArray(tool.departments) ? tool.departments : [],
      audienceScope: tool.hasSpecificUserAudience ? 'specific_users' : 'departments',
      specificUserEmails: [],
      manualSpecificUserEmails: '',
      salesStages: Array.isArray(tool.salesStages) ? tool.salesStages : [],
      integrations: Array.isArray(tool.integrations) ? tool.integrations : [],
      whatItDoes: Array.isArray(tool.whatItDoes) ? tool.whatItDoes.join('\n') : '',
      goodFitScenarios: Array.isArray(tool.goodFitScenarios) ? tool.goodFitScenarios.join('\n') : '',
      notGoodFitScenarios: Array.isArray(tool.notGoodFitScenarios) ? tool.notGoodFitScenarios.join('\n') : '',
      previewUrl: tool.previewUrl || '',
      socialLinks: tool.socialLinks ? JSON.stringify(tool.socialLinks) : '',
      requireAccessRequest: false,
      botIntegration: emptyBotIntegration(),
    });

    if (userRole === 'admin' && tool.hasSpecificUserAudience && tool.id) {
      Promise.all([
        garageAPI.getToolSpecificUsers(tool.id),
        loadAudienceUsers(tool.departments || []),
      ]).then(([audience, workdayUsers]) => {
        const selectedEmails = (audience?.userEmails || []).map((email) => email.toLowerCase());
        const selectedProfiles = workdayUsers.filter((profile) =>
          selectedEmails.includes(String(profile.email || '').toLowerCase())
        );
        const selectedDepartments = Array.from(new Set(
          selectedProfiles.map((profile) => profile.businessUnit).filter(Boolean)
        ));
        setToolFormData((previous) => ({
          ...previous,
          departments: previous.departments.length > 0
            ? previous.departments
            : selectedDepartments,
          audienceScope: 'specific_users',
          specificUserEmails: audience?.userEmails || [],
        }));
      }).catch((error) => {
        console.error('Failed to load specific-user audience', error);
      });
    }

    if (
      config.BOT_INTEGRATIONS_ENABLED
      && userRole === 'admin'
      && tool.toolType === 'App'
      && (tool.appSubtype || 'web') === 'bot'
    ) {
      garageAPI.getBotIntegration(tool.id).then((integration) => {
        setToolFormData((previous) => ({
          ...previous,
          botIntegration: {
            ...emptyBotIntegration(),
            ...integration,
            serverUrl: integration.serverUrl || 'https://platform.ringcentral.com',
            webUrlTemplate: integration.webUrlTemplate || 'https://app.ringcentral.com/l/messages/{conversationId}',
            rcAppUrlTemplate: integration.rcAppUrlTemplate || '',
            fallbackUrl: integration.fallbackUrl || '',
          },
        }));
      }).catch((error) => {
        console.error('Failed to load bot integration metadata', error);
      });
    }

    // Load session config if editing an app
    if (tool.toolType === 'App' && tool.id) {
      loadSessionConfig(tool.id);
    } else {
      // Reset session config for Gems or new tools
      setSessionConfig({
        sourceTableName: '',
        sessionIdColumn: '',
        userEmailColumn: '',
        sessionStartColumn: '',
        sessionEndColumn: '',
        incrementalColumn: '',
        isActive: false
      });
    }

    setShowToolForm(true);

    // Load access-control opt-in (separate registry; default unchecked if unregistered)
    if (config.ACCESS_REQUESTS_ENABLED && tool.id) {
      garageAPI
        .listAccessResources({ resource_type: 'garage_tool' })
        .then((resources) => {
          const match = (resources || []).find((r) => r.externalRef === tool.id);
          setToolFormData((prev) => ({
            ...prev,
            requireAccessRequest: Boolean(match?.isAccessControlled),
          }));
        })
        .catch((err) => {
          console.warn('Failed to load access control flag for tool', err);
        });
    }
  };

  const handleCreateOrUpdateTool = async (e) => {
    e.preventDefault();
    if (isSaving) return;
    setIsSaving(true);
    try {
      if (toolFormData.toolType === 'Gem' && !(toolFormData.url || '').trim()) {
        setAlertModal({
          isOpen: true,
          title: 'Validation Error',
          message: 'URL is required for Gems.',
          variant: 'error'
        });
        return;
      }

      const botIntegration = toolFormData.botIntegration || emptyBotIntegration();
      const shouldConfigureBot = Boolean(
        config.BOT_INTEGRATIONS_ENABLED
        && userRole === 'admin'
        && toolFormData.toolType === 'App'
        && toolFormData.appSubtype === 'bot'
        && (
          botIntegration.configured
          || (botIntegration.botExtensionId || '').trim()
          || (botIntegration.botEmail || '').trim()
        )
      );
      if (shouldConfigureBot && (!(botIntegration.botExtensionId || '').trim() || !(botIntegration.botEmail || '').trim())) {
        setAlertModal({
          isOpen: true,
          title: 'Bot configuration incomplete',
          message: 'Bot extension ID and bot email are required for dynamic conversation launch.',
          variant: 'error',
        });
        return;
      }
      // Arrays are already in correct format from MultiSelectDropdown
      const tagsArray = Array.isArray(toolFormData.tags) ? toolFormData.tags : [];
      const rolesArray = Array.isArray(toolFormData.roles) ? toolFormData.roles : [];
      const journeysArray = Array.isArray(toolFormData.journeys) ? toolFormData.journeys : [];
      const capabilitiesArray = Array.isArray(toolFormData.capabilities) ? toolFormData.capabilities : [];
      const personasArray = Array.isArray(toolFormData.personas) ? toolFormData.personas : [];
      const selectedDepartments = Array.isArray(toolFormData.departments) ? toolFormData.departments : [];
      const usesSpecificUserAudience = toolFormData.toolType === 'App'
        && toolFormData.audienceScope === 'specific_users';
      const pendingManualEmails = parseAudienceEmails(toolFormData.manualSpecificUserEmails);
      const invalidManualEmails = pendingManualEmails.filter((email) => !isValidAudienceEmail(email));
      if (usesSpecificUserAudience && invalidManualEmails.length > 0) {
        setAlertModal({
          isOpen: true,
          title: 'Invalid email address',
          message: `Check these values and try again: ${invalidManualEmails.join(', ')}`,
          variant: 'error',
        });
        return;
      }
      const specificUserEmails = usesSpecificUserAudience
        ? Array.from(new Set([
            ...(Array.isArray(toolFormData.specificUserEmails) ? toolFormData.specificUserEmails : []),
            ...pendingManualEmails,
          ]))
        : [];
      if (usesSpecificUserAudience && specificUserEmails.length === 0) {
        setAlertModal({
          isOpen: true,
          title: 'Specific users required',
          message: 'Select or paste at least one eligible user email.',
          variant: 'error',
        });
        return;
      }
      const salesStagesArray = Array.isArray(toolFormData.salesStages) ? toolFormData.salesStages : [];
      const integrationsArray = Array.isArray(toolFormData.integrations) ? toolFormData.integrations : [];

      const whatItDoesArray = (toolFormData.whatItDoes || '')
        .split('\n')
        .map(line => line.trim())
        .filter(line => line.length > 0);

      const goodFitScenariosArray = (toolFormData.goodFitScenarios || '')
        .split('\n')
        .map(line => line.trim())
        .filter(line => line.length > 0);

      const notGoodFitScenariosArray = (toolFormData.notGoodFitScenarios || '')
        .split('\n')
        .map(line => line.trim())
        .filter(line => line.length > 0);

      let socialLinksObj = null;
      if (toolFormData.socialLinks && toolFormData.socialLinks.trim()) {
        try {
          socialLinksObj = JSON.parse(toolFormData.socialLinks);
        } catch (e) {
          setAlertModal({
            isOpen: true,
            title: 'Invalid JSON',
            message: 'Invalid JSON for social links. Please fix and try again.',
            variant: 'error'
          });
          return;
        }
      }

      const stepsArray = parseStepsText(toolFormData.steps);

      const payload = {
        title: toolFormData.title,
        description: toolFormData.description,
        url: (toolFormData.url || '').trim(),
        imageUrl: toolFormData.imageUrl,
        toolType: toolFormData.toolType,
        appSubtype: toolFormData.toolType === 'App' ? (toolFormData.appSubtype || 'web') : undefined,
        rcAppUrl: toolFormData.toolType === 'App' && toolFormData.appSubtype === 'bot'
          ? ((toolFormData.rcAppUrl || '').trim() || undefined)
          : (toolFormData.toolType === 'App' ? null : undefined),
        steps: toolFormData.toolType === 'App' && toolFormData.appSubtype === 'bot'
          ? stepsArray
          : (toolFormData.toolType === 'App' ? [] : undefined),
        status: toolFormData.status,
        // Maker emails optional — default to the creating admin when left blank
        makerEmails: toolFormData.makerEmail?.trim()
          ? toolFormData.makerEmail.split(',').map(email => email.trim()).filter(e => e)
          : (userEmail ? [userEmail] : []),
        tags: tagsArray,
        roles: rolesArray,
        journeys: journeysArray,
        capabilities: capabilitiesArray.length > 0 ? capabilitiesArray : undefined,
        persona: (toolFormData.persona || '').trim() || undefined,
        createdBy: userEmail,  // Backend accepts both createdBy and createdByEmail
        createdByName: userEmail?.split('@')[0],
        // New enhanced fields with null safety
        environment: (toolFormData.environment || '').trim() || undefined,
        model: (toolFormData.model || '').trim() || undefined,
        personas: personasArray.length > 0 ? personasArray : undefined,
        departments: selectedDepartments,
        salesStages: salesStagesArray.length > 0 ? salesStagesArray : undefined,
        integrations: integrationsArray.length > 0 ? integrationsArray : undefined,
        whatItDoes: whatItDoesArray.length > 0 ? whatItDoesArray : undefined,
        goodFitScenarios: goodFitScenariosArray.length > 0 ? goodFitScenariosArray : undefined,
        notGoodFitScenarios: notGoodFitScenariosArray.length > 0 ? notGoodFitScenariosArray : undefined,
        previewUrl: (toolFormData.previewUrl || '').trim() || undefined,
        socialLinks: socialLinksObj || undefined,
        specificUserEmails: userRole === 'admin' ? specificUserEmails : undefined,
        specificUsersDepartments: userRole === 'admin' && usesSpecificUserAudience
          ? selectedDepartments
          : undefined,
      };

      let savedTool;
      let savedToolId;
      let accessControlWarning = null;
      const wasEditing = Boolean(editingTool);
      if (editingTool) {
        savedTool = await garageAPI.updateTool(editingTool.id, payload);
        savedToolId = editingTool.id;

        // Handle session config update for Apps
        if (toolFormData.toolType === 'App' && sessionConfig.sourceTableName && sessionConfig.isActive) {
          await saveSessionConfig(savedToolId);
        }
      } else {
        savedTool = await garageAPI.createTool(payload);
        savedToolId = savedTool.id;

        // Create session config for Apps if configured
        if (toolFormData.toolType === 'App' && sessionConfig.sourceTableName && sessionConfig.isActive) {
          await saveSessionConfig(savedToolId);
        }
      }

      if (usesSpecificUserAudience && !savedTool?.hasSpecificUserAudience) {
        throw new Error(
          'The tool was saved, but its specific-user audience was not persisted. '
          + 'Please keep this form open and try saving the audience again.'
        );
      }

      // Sync access-control opt-in (registry upsert; skipped when feature kill switch off)
      if (config.ACCESS_REQUESTS_ENABLED && savedToolId) {
        try {
          await garageAPI.upsertAccessResource({
            resourceType: 'garage_tool',
            externalRef: savedToolId,
            displayName: toolFormData.title,
            isAccessControlled: Boolean(toolFormData.requireAccessRequest),
            provisioner: 'manual',
          });
        } catch (accessErr) {
          console.error('Failed to update access control flag', accessErr);
          accessControlWarning = formatApiError(
            accessErr,
            'Tool saved, but updating “Require access request” failed. Try editing the tool again.'
          );
        }
      }

      if (shouldConfigureBot && savedToolId) {
        try {
          await garageAPI.saveBotIntegration(savedToolId, {
            botExtensionId: botIntegration.botExtensionId.trim(),
            botEmail: botIntegration.botEmail.trim(),
            serverUrl: (botIntegration.serverUrl || 'https://platform.ringcentral.com').trim(),
            webLaunchEnabled: Boolean(botIntegration.webLaunchEnabled),
            rcAppLaunchEnabled: Boolean(botIntegration.rcAppLaunchEnabled),
            webUrlTemplate: (botIntegration.webUrlTemplate || '').trim() || null,
            rcAppUrlTemplate: (botIntegration.rcAppUrlTemplate || '').trim() || null,
            fallbackUrl: (botIntegration.fallbackUrl || '').trim() || null,
            enabled: Boolean(botIntegration.enabled),
            verifyCredentials: true,
            credentials: botIntegration.provideCredentials ? {
              clientId: botIntegration.credentials?.clientId || null,
              clientSecret: botIntegration.credentials?.clientSecret || null,
              accessToken: botIntegration.credentials?.accessToken || null,
              refreshToken: botIntegration.credentials?.refreshToken || null,
              jwtToken: botIntegration.credentials?.jwtToken || null,
            } : null,
          });
        } catch (botError) {
          console.error('Tool saved, but bot integration configuration failed', botError);
          accessControlWarning = formatApiError(
            botError,
            'Tool saved, but the RingCentral bot credentials could not be verified or stored.'
          );
        }
      }

      const convertedDynamicBotToNonBot = Boolean(
        config.BOT_INTEGRATIONS_ENABLED
        && userRole === 'admin'
        && editingTool?.id
        && editingTool.toolType === 'App'
        && (editingTool.appSubtype || 'web') === 'bot'
        && !(toolFormData.toolType === 'App' && toolFormData.appSubtype === 'bot')
        && (botIntegration.configured || editingTool.botLaunchOptions?.dynamic)
      );
      if (convertedDynamicBotToNonBot) {
        try {
          await garageAPI.deleteBotIntegration(editingTool.id);
        } catch (botDeleteError) {
          console.error('Tool updated, but encrypted bot integration cleanup failed', botDeleteError);
          accessControlWarning = formatApiError(
            botDeleteError,
            'Tool updated, but its encrypted bot integration could not be removed.'
          );
        }
      }

      setShowToolForm(false);
      setEditingTool(null);
      setAlertModal({
        isOpen: true,
        title: accessControlWarning ? 'Saved with warning' : 'Success',
        message: accessControlWarning
          || (wasEditing ? 'Tool updated successfully!' : 'Tool created successfully!'),
        variant: accessControlWarning ? 'error' : 'success'
      });
      setToolFormData({
        title: '',
        description: '',
        url: '',
        imageUrl: '',
        toolType: 'App',
        appSubtype: 'web',
        rcAppUrl: '',
        steps: '',
        status: 'draft',
        makerEmail: '',
        tags: '',
        roles: '',
        journeys: '',
        capabilities: [],
        persona: '',
        // Reset new enhanced fields
        environment: '',
        model: '',
        personas: '',
        departments: [],
        audienceScope: 'departments',
        specificUserEmails: [],
        manualSpecificUserEmails: '',
        salesStages: '',
        integrations: '',
        whatItDoes: '',
        goodFitScenarios: '',
        notGoodFitScenarios: '',
        previewUrl: '',
        socialLinks: '',
        requireAccessRequest: false,
        botIntegration: emptyBotIntegration(),
      });

      // Reset session config
      setSessionConfig({
        sourceTableName: '',
        sessionIdColumn: '',
        userEmailColumn: '',
        sessionStartColumn: '',
        sessionEndColumn: '',
        incrementalColumn: '',
        isActive: false
      });
      setConnectionTestResult(null);

      loadTools({ silent: true });
    } catch (error) {
      console.error('Failed to save tool:', error);
      setAlertModal({
        isOpen: true,
        title: 'Error',
        message: formatApiError(error, 'Failed to save tool. Please try again.'),
        variant: 'error'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleTestBotIntegration = async () => {
    if (isTestingBot) return;
    const integration = toolFormData.botIntegration || emptyBotIntegration();
    const identifier = (integration.botExtensionId || integration.botEmail || '').trim();
    const credentials = integration.provideCredentials ? {
      clientId: integration.credentials?.clientId || null,
      clientSecret: integration.credentials?.clientSecret || null,
      accessToken: integration.credentials?.accessToken || null,
      refreshToken: integration.credentials?.refreshToken || null,
      jwtToken: integration.credentials?.jwtToken || null,
    } : null;
    if (!identifier) {
      setAlertModal({
        isOpen: true,
        title: 'Bot identifier required',
        message: 'Enter the bot extension ID/number or bot email before testing the connection.',
        variant: 'error',
      });
      return;
    }
    setIsTestingBot(true);
    try {
      const result = await garageAPI.testBotCredential({
        identifier,
        serverUrl: (integration.serverUrl || '').trim() || null,
        credentials,
      });
      setAlertModal({
        isOpen: true,
        title: 'Connection verified',
        message: `Authenticated as ${result.name || result.email || result.extensionNumber || result.extensionId || 'the configured bot'}.`,
        variant: 'success',
      });
    } catch (error) {
      setAlertModal({
        isOpen: true,
        title: 'Connection failed',
        message: formatApiError(error, 'RingCentral rejected the bot credentials or permissions.'),
        variant: 'error',
      });
    } finally {
      setIsTestingBot(false);
    }
  };

  const handleFetchBotCredential = async () => {
    if (isFetchingBotCredential) return;
    const integration = toolFormData.botIntegration || emptyBotIntegration();
    const identifier = (integration.botExtensionId || integration.botEmail || '').trim();
    if (!identifier) {
      setAlertModal({
        isOpen: true,
        title: 'Bot identifier required',
        message: 'Enter the bot extension ID/number or bot email, then fetch the registered bot.',
        variant: 'error',
      });
      return;
    }
    setIsFetchingBotCredential(true);
    try {
      const result = await garageAPI.lookupBotCredential(identifier);
      if (!result.found) {
        setAlertModal({
          isOpen: true,
          title: 'No registered credentials found',
          message: 'This bot has not been provisioned by its deployment in this environment. Ask the bot owner to run its provisioning step, or use the secure credentials section once if you are authorized to provide them.',
          variant: 'error',
        });
        return;
      }
      setToolFormData((previous) => ({
        ...previous,
        botIntegration: {
          ...(previous.botIntegration || emptyBotIntegration()),
          credentialAvailable: true,
          credentialRegistryId: result.credentialRegistryId,
          credentialStatus: result.credentialStatus || 'registered',
          credentialVersion: result.credentialVersion || 0,
          botExtensionId: result.botExtensionNumber || result.botExtensionId || previous.botIntegration?.botExtensionId || '',
          botEmail: result.botEmail || previous.botIntegration?.botEmail || '',
          serverUrl: result.serverUrl || previous.botIntegration?.serverUrl || 'https://platform.ringcentral.com',
        },
      }));
      setAlertModal({
        isOpen: true,
        title: 'Registered bot fetched',
        message: `Found ${result.botName || result.botEmail || 'the bot'}${result.botExtensionNumber ? ` (extension ${result.botExtensionNumber})` : ''}. Complete the tool details and select Update Tool to attach this bot to the catalog entry.`,
        variant: 'success',
      });
    } catch (error) {
      setAlertModal({
        isOpen: true,
        title: 'Could not fetch registered bot',
        message: formatApiError(error, 'Unable to read the registered bot metadata.'),
        variant: 'error',
      });
    } finally {
      setIsFetchingBotCredential(false);
    }
  };

  const handleDeleteTool = async (toolId) => {
    setConfirmModal({
      isOpen: true,
      title: 'Delete Tool',
      message: 'Are you sure you want to delete this tool? This action cannot be undone.',
      variant: 'danger',
      onConfirm: async () => {
        try {
          await garageAPI.deleteTool(toolId);
          loadTools({ silent: true });
          setConfirmModal({ ...confirmModal, isOpen: false });
          setAlertModal({
            isOpen: true,
            title: 'Success',
            message: 'Tool deleted successfully!',
            variant: 'success'
          });
        } catch (error) {
          console.error('Failed to delete tool:', error);
          setConfirmModal({ ...confirmModal, isOpen: false });
          setAlertModal({
            isOpen: true,
            title: 'Error',
            message: formatApiError(error, 'Failed to delete tool. Please try again.'),
            variant: 'error'
          });
        }
      }
    });
  };

  // Maker Functions
  const handleEditMaker = (maker) => {
    setEditingMaker(maker);
    setMakerFormData({
      email: maker.email || '',
      displayName: maker.displayName || '',
      firstName: maker.firstName || '',
      lastName: maker.lastName || '',
      jobTitle: maker.jobTitle || '',
      department: maker.department || '',
      team: maker.team || '',
      location: maker.location || '',
      bio: maker.bio || '',
      profileImageUrl: maker.profileImageUrl || maker.profile_image_url || '',
      slackHandle: maker.slackHandle || maker.slack_handle || '',
      focusAreas: maker.focusAreas?.join(', ') || ''
    });
    setShowMakerForm(true);
  };

  const handleCreateOrUpdateMaker = async (e) => {
    e.preventDefault();
    try {
      const focusAreasArray = makerFormData.focusAreas
        .split(',')
        .map(area => area.trim())
        .filter(area => area.length > 0);

      const payload = {
        email: makerFormData.email,
        displayName: makerFormData.displayName,
        firstName: makerFormData.firstName,
        lastName: makerFormData.lastName,
        jobTitle: makerFormData.jobTitle,
        department: makerFormData.department,
        team: makerFormData.team,
        location: makerFormData.location,
        bio: makerFormData.bio,
        profileImageUrl: makerFormData.profileImageUrl,
        slackHandle: makerFormData.slackHandle,
        focusAreas: focusAreasArray,
        isVerified: true
      };

      if (editingMaker) {
        await garageAPI.updateMaker(editingMaker.email, payload);
        setShowMakerForm(false);
        setEditingMaker(null);
        setMakerFormData({
          email: '',
          displayName: '',
          firstName: '',
          lastName: '',
          jobTitle: '',
          department: '',
          team: '',
          location: '',
          bio: '',
          profileImageUrl: '',
          slackHandle: '',
          focusAreas: ''
        });
        loadMakers({ silent: true });
        setAlertModal({
          isOpen: true,
          title: 'Success',
          message: 'Maker updated successfully!',
          variant: 'success'
        });
      } else {
        await garageAPI.createMakerAdmin(payload);
        setShowMakerForm(false);
        setEditingMaker(null);
        setMakerFormData({
          email: '',
          displayName: '',
          firstName: '',
          lastName: '',
          jobTitle: '',
          department: '',
          team: '',
          location: '',
          bio: '',
          profileImageUrl: '',
          slackHandle: '',
          focusAreas: ''
        });
        loadMakers({ silent: true });
        setAlertModal({
          isOpen: true,
          title: 'Success',
          message: 'Maker created successfully!',
          variant: 'success'
        });
      }
    } catch (error) {
      console.error('Failed to save maker:', error);
      const detail = error.response?.data?.detail;
      let errorMessage = formatApiError(error, 'Failed to save maker. Please try again.');
      if (typeof detail === 'string' && detail === 'Maker with this email already exists') {
        errorMessage = 'A maker with this email already exists.';
      }
      setAlertModal({
        isOpen: true,
        title: 'Error',
        message: errorMessage,
        variant: 'error'
      });
    }
  };

  const handleDeleteMaker = async (maker) => {
    setConfirmModal({
      isOpen: true,
      title: 'Delete Maker',
      message: `Are you sure you want to delete maker ${maker.displayName}? This will remove them from all tools.`,
      variant: 'danger',
      onConfirm: async () => {
        try {
          await garageAPI.deleteMaker(maker.email);
          loadMakers({ silent: true });
          setConfirmModal({ ...confirmModal, isOpen: false });
          setAlertModal({
            isOpen: true,
            title: 'Success',
            message: 'Maker deleted successfully!',
            variant: 'success'
          });
        } catch (error) {
          console.error('Failed to delete maker:', error);
          setConfirmModal({ ...confirmModal, isOpen: false });
          setAlertModal({
            isOpen: true,
            title: 'Error',
            message: formatApiError(error, 'Failed to delete maker. Please try again.'),
            variant: 'error'
          });
        }
      }
    });
  };

  // User Functions
  const personaTelemetryId = (value) => {
    const needle = String(value || '').trim().toLowerCase();
    return (dynamicOptions.personas || []).find(
      (persona) =>
        String(persona.value || '').trim().toLowerCase() === needle
        || String(persona.label || '').trim().toLowerCase() === needle
    )?.id;
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setUserFormData({
      userEmail: user.userEmail || '',
      displayName: user.displayName || '',
      role: user.role || 'user',
      onboardingStatus: user.onboardingStatus || 'completed',
      department: user.department || '',
      workdayBusinessUnit: user.workdayBusinessUnit || '',
      accessPersona: user.accessPersona || '',
      accessPersonas: user.accessPersonas
        || (user.accessPersona ? [user.accessPersona] : []),
      grantedToolIds: Array.isArray(user.grantedToolIds) ? user.grantedToolIds : [],
      canViewAdminActivity: Boolean(user.canViewAdminActivity) && user.role === 'admin',
    });
    setShowUserForm(true);
    emitGovernanceTelemetry('admin_access_assignment_started', {
      actorType: userRole,
      previousRole: user.role || 'user',
      personaId: personaTelemetryId(user.accessPersona),
    });
  };

  const canEditUserAccess = (user) => {
    if (!mayManageUsers) return false;
    if (!domainAdmin) return true;
    if ((user.userEmail || '').toLowerCase() === (userEmail || '').toLowerCase()) {
      return false;
    }
    if (['admin', 'analyst_reviewer'].includes(user.role)) return false;
    if (user.role === 'persona_admin') {
      return (user.accessPersona || '').toLowerCase()
        === (accessPersona || '').toLowerCase();
    }
    return true;
  };

  const userAccessGuardrailReason = (user) => {
    if ((user.userEmail || '').toLowerCase() === (userEmail || '').toLowerCase()) {
      return 'self_management';
    }
    if (['admin', 'analyst_reviewer'].includes(user.role)) {
      return 'full_admin_target';
    }
    if (
      user.role === 'persona_admin'
      && (user.accessPersona || '').toLowerCase()
        !== (accessPersona || '').toLowerCase()
    ) {
      return 'cross_persona';
    }
    return 'unauthorized';
  };

  const saveUser = async () => {
    try {
      const payload = {
        userEmail: userFormData.userEmail,
        ...(editingUser
          ? {
              expectedRole: editingUser.role || 'user',
              expectedAccessPersona: editingUser.accessPersona || null,
              expectedAccessPersonas: editingUser.accessPersonas
                || (editingUser.accessPersona ? [editingUser.accessPersona] : []),
            }
          : {}),
        displayName: userFormData.displayName,
        role: userFormData.role,
        onboardingStatus: userFormData.onboardingStatus,
        department: userFormData.department || null,
        workdayBusinessUnit: userFormData.workdayBusinessUnit || null,
        accessPersona: userFormData.accessPersonas[0] || null,
        accessPersonas: userFormData.role === 'persona_admin'
          ? userFormData.accessPersonas
          : [],
        grantedToolIds: userFormData.grantedToolIds || [],
        canViewAdminActivity: userFormData.role === 'admin' && Boolean(userFormData.canViewAdminActivity),
      };

      if (editingUser) {
        await garageAPI.updateUserProfile(editingUser.userEmail, payload);
        emitGovernanceTelemetry('admin_access_assignment_succeeded', {
          actorType: userRole,
          previousRole: editingUser.role || 'user',
          selectedRole: userFormData.role,
          personaId: personaTelemetryId(
            domainAdmin ? accessPersona : userFormData.accessPersona
          ),
          confirmationRequired:
            editingUser.role !== userFormData.role
            || (editingUser.accessPersona || '')
              !== (domainAdmin ? accessPersona : userFormData.accessPersona || ''),
          outcome: 'succeeded',
        });
        setAlertModal({
          isOpen: true,
          title: 'Success',
          message: 'User updated successfully!',
          variant: 'success'
        });
      } else {
        await garageAPI.createUserProfile(payload);
        setAlertModal({
          isOpen: true,
          title: 'Success',
          message: 'User created successfully!',
          variant: 'success'
        });
      }

      setShowUserForm(false);
      setEditingUser(null);
      setUserFormData({
        userEmail: '',
        displayName: '',
        role: 'user',
        onboardingStatus: 'completed',
        department: '',
        workdayBusinessUnit: '',
        accessPersona: '',
        accessPersonas: [],
        grantedToolIds: [],
        canViewAdminActivity: false,
      });
      if (
        onActivityAccessChange
        && (userFormData.userEmail || '').toLowerCase() === (userEmail || '').toLowerCase()
      ) {
        onActivityAccessChange(userFormData.role === 'admin' && Boolean(userFormData.canViewAdminActivity));
      }
      loadUsers({ silent: true });
    } catch (error) {
      console.error('Failed to save user:', error);
      emitGovernanceTelemetry('admin_access_assignment_failed', {
        actorType: userRole,
        previousRole: editingUser?.role || 'user',
        selectedRole: userFormData.role,
        personaId: personaTelemetryId(
          domainAdmin ? accessPersona : userFormData.accessPersona
        ),
        outcome: 'failed',
        failureReason: governanceFailureReason(error),
      });
      const detail = error.response?.data?.detail;
      let errorMessage = formatApiError(error, 'Failed to save user. Please try again.');
      if (error.response?.status === 409 && editingUser) {
        emitGovernanceTelemetry('admin_guardrail_triggered', {
          actorType: userRole,
          previousRole: editingUser.role || 'user',
          selectedRole: userFormData.role,
          personaId: personaTelemetryId(
            domainAdmin ? accessPersona : userFormData.accessPersona
          ),
          outcome: 'blocked',
          guardrailReason: governanceGuardrailReason(error),
        });
        try {
          const currentUser = await garageAPI.getUserProfile(editingUser.userEmail);
          handleEditUser(currentUser);
          errorMessage = typeof detail === 'string'
            ? detail
            : 'This access record changed. Review the refreshed values and try again.';
        } catch (refreshError) {
          console.error('Failed to refresh stale user access:', refreshError);
        }
      }
      if (typeof detail === 'string' && detail === 'User profile already exists') {
        errorMessage = 'A user with this email already exists.';
      }
      setAlertModal({
        isOpen: true,
        title: 'Error',
        message: errorMessage,
        variant: 'error'
      });
    }
  };

  const handleCreateOrUpdateUser = (e) => {
    e.preventDefault();
    if (
      userFormData.role === 'persona_admin'
      && userFormData.accessPersonas.length === 0
    ) {
      setAlertModal({
        isOpen: true,
        title: 'Persona required',
        message: 'Select at least one persona for Persona Admin access.',
        variant: 'warning',
      });
      return;
    }
    const previousRole = editingUser?.role || 'user';
    const previousPersonas = editingUser?.accessPersonas
      || (editingUser?.accessPersona ? [editingUser.accessPersona] : []);
    const nextPersonas = domainAdmin
      ? adminPersonaScopes
      : userFormData.accessPersonas;
    const roleChanged = previousRole !== userFormData.role;
    const personaChanged = JSON.stringify(previousPersonas) !== JSON.stringify(nextPersonas);
    const sensitivePromotion = ['persona_admin', 'admin'].includes(userFormData.role)
      && roleChanged;
    const roleRank = {
      user: 0,
      analyst_reviewer: 1,
      persona_admin: 2,
      admin: 3,
    };
    const demotionOrRemoval = roleChanged
      && roleRank[userFormData.role] < roleRank[previousRole];

    if (editingUser && (sensitivePromotion || demotionOrRemoval || personaChanged)) {
      emitGovernanceTelemetry('admin_access_confirmation_viewed', {
        actorType: userRole,
        previousRole,
        selectedRole: userFormData.role,
        personaId: personaTelemetryId(nextPersonas[0]),
        changeType: sensitivePromotion
          ? 'promotion'
          : demotionOrRemoval
            ? 'demotion'
            : 'persona_change',
        confirmationReason: sensitivePromotion
          ? 'administrative_promotion'
          : 'demotion_or_removal',
        confirmationRequired: true,
      });
      setConfirmModal({
        isOpen: true,
        title: 'Confirm access change',
        message: `${editingUser.displayName || editingUser.userEmail} will change from ${
          ACCESS_ROLE_LABELS[previousRole] || previousRole
        } to ${ACCESS_ROLE_LABELS[userFormData.role] || userFormData.role}${
          nextPersonas.length ? ` for ${nextPersonas.join(', ')}` : ''
        }. This takes effect immediately and is recorded in the audit log.`,
        variant: sensitivePromotion ? 'warning' : 'default',
        onConfirm: async () => {
          setConfirmModal((current) => ({ ...current, isOpen: false }));
          await saveUser();
        },
      });
      return;
    }
    saveUser();
  };

  const toggleUserSort = (key) => {
    setUserSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: 'asc' }
    );
  };

  const isUserColumnVisible = (key) => visibleUserColumns.includes(key);

  const toggleUserColumn = (key) => {
    setVisibleUserColumns((current) =>
      current.includes(key)
        ? current.filter((column) => column !== key)
        : [...current, key]
    );
  };

  const renderUserSortHeader = (label, key) => {
    const active = userSort.key === key;
    const SortIcon = active ? (userSort.dir === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
    return (
      <th
        className={`garage-sortable-th${active ? ' is-active' : ''}`}
        onClick={() => toggleUserSort(key)}
        title={`Sort by ${label}`}
        aria-sort={active ? (userSort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      >
        <span className="garage-sortable-th-inner">
          {label}
          <SortIcon size={13} className="garage-sort-icon" aria-hidden="true" />
        </span>
      </th>
    );
  };

  const handleDeleteUser = async (user) => {
    setConfirmModal({
      isOpen: true,
      title: 'Delete User',
      message: `Are you sure you want to delete user ${user.displayName || user.userEmail}?`,
      variant: 'danger',
      onConfirm: async () => {
        try {
          await garageAPI.deleteUserProfile(user.userEmail);
          loadUsers({ silent: true });
          setConfirmModal({ ...confirmModal, isOpen: false });
          setAlertModal({
            isOpen: true,
            title: 'Success',
            message: 'User deleted successfully!',
            variant: 'success'
          });
        } catch (error) {
          console.error('Failed to delete user:', error);
          setConfirmModal({ ...confirmModal, isOpen: false });
          setAlertModal({
            isOpen: true,
            title: 'Error',
            message: formatApiError(error, 'Failed to delete user. Please try again.'),
            variant: 'error'
          });
        }
      }
    });
  };

  // Filter and pagination logic
  const getFilteredItems = () => {
    let items = [];

    if (activeTab === 'tools') {
      // Persona Admins only manage their scoped tools/gems - other domains (including
      // tools with no persona set) are excluded entirely, not just locked.
      const scopedTools = domainAdmin ? tools.filter(canEditTool) : tools;
      items = scopedTools.filter(tool => {
        const matchesSearch =
          (tool.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
           tool.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
           tool.makerEmail?.toLowerCase().includes(searchQuery.toLowerCase()) ||
           tool.makerName?.toLowerCase().includes(searchQuery.toLowerCase()));
        if (!matchesSearch) return false;
        if (toolPersonaFilter) {
          const personas = [tool.persona, ...(Array.isArray(tool.personas) ? tool.personas : [])];
          const needle = toolPersonaFilter.trim().toLowerCase();
          if (!personas.some((persona) => String(persona || '').trim().toLowerCase() === needle)) return false;
        }
        return true;
      });
    } else if (activeTab === 'users') {
      const q = searchQuery.toLowerCase();
      items = users.filter((user) => {
        const teamLabel = resolveOnboardingOptionLabel(user.persona, dynamicOptions.onboardingTeams) || '';
        const jobRoleLabel = resolveOnboardingOptionLabel(user.userRole, dynamicOptions.onboardingJobRoles) || '';
        const accessLabel = ACCESS_ROLE_LABELS[user.role] || user.role || '';
        const matchesSearch = (
          user.displayName?.toLowerCase().includes(q) ||
          user.userEmail?.toLowerCase().includes(q) ||
          user.role?.toLowerCase().includes(q) ||
          accessLabel.toLowerCase().includes(q) ||
          (user.persona || '').toLowerCase().includes(q) ||
          teamLabel.toLowerCase().includes(q) ||
          (user.userRole || '').toLowerCase().includes(q) ||
          jobRoleLabel.toLowerCase().includes(q) ||
          (user.accessPersonas || [user.accessPersona])
            .filter(Boolean)
            .join(' ')
            .toLowerCase()
            .includes(q) ||
          (user.workdayBusinessUnit || '').toLowerCase().includes(q)
        );
        if (!matchesSearch) return false;
        if (userRoleFilter && user.role !== userRoleFilter) return false;
        if (
          personaFilter
          && !(user.accessPersonas || [user.accessPersona])
            .filter(Boolean)
            .some(
              (persona) => persona.toLowerCase() === personaFilter.toLowerCase()
            )
        ) return false;
        return true;
      });
      const { key, dir } = userSort;
      const mul = dir === 'asc' ? 1 : -1;
      items = [...items].sort((a, b) => {
        const av = key === 'accessPersona'
          ? (a.accessPersonas || [a.accessPersona]).filter(Boolean).join(', ')
          : a?.[key] ?? '';
        const bv = key === 'accessPersona'
          ? (b.accessPersonas || [b.accessPersona]).filter(Boolean).join(', ')
          : b?.[key] ?? '';
        if (key === 'loginCount') {
          return (Number(av) - Number(bv)) * mul;
        }
        if (key === 'lastLoginAt') {
          const at = av ? new Date(av).getTime() : 0;
          const bt = bv ? new Date(bv).getTime() : 0;
          return (at - bt) * mul;
        }
        return String(av).localeCompare(String(bv), undefined, { sensitivity: 'base' }) * mul;
      });
    }

    return items;
  };

  const getPaginatedItems = () => {
    const filtered = getFilteredItems();
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filtered.slice(startIndex, endIndex);
  };

  const getTotalPages = () => {
    const filtered = getFilteredItems();
    return Math.ceil(filtered.length / itemsPerPage);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(Number(value));
    setCurrentPage(1); // Reset to first page
  };

  const handleSearchChange = (value) => {
    setSearchQuery(value);
    setCurrentPage(1); // Reset to first page when searching
  };

  // Reset pagination when tab changes
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchQuery('');
    setToolPersonaFilter('');
    setUserRoleFilter('');
    setCurrentPage(1);
  };

  return (
    <div className="garage-content">
      <div className="garage-page-hero">
        <div>
          <h1 className="garage-page-title">Admin Dashboard</h1>
          <p className="garage-page-subtitle">Manage tools, configuration, and Garage operations</p>
        </div>
        {activeTab === 'tools' && (
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              className="garage-btn garage-btn-secondary"
              onClick={() => setShowTour(true)}
              title="Take a quick tour"
            >
              <Sparkles size={16} />
              Help
            </button>
            <button
              className="garage-btn garage-btn-secondary"
              onClick={() => loadTools()}
              title="Refresh tools list"
            >
              <RefreshCw size={16} />
              Refresh
            </button>
            <button
              className="garage-btn garage-btn-primary"
              data-tour="admin-add-tool"
              onClick={() => {
                setEditingTool(null);
                setToolFormData({
                  title: '',
                  description: '',
                  url: '',
                  imageUrl: '',
                  toolType: 'App',
                  appSubtype: 'web',
                  rcAppUrl: '',
                  steps: '',
                  status: 'draft',
                  makerEmail: '',
                  tags: [],
                  roles: [],
                  journeys: [],
                  capabilities: [],
                  persona: adminPersonaScope || '',
                  // Include all enhanced fields to prevent undefined errors
                  environment: '',
                  model: '',
                  personas: [],
                  departments: [],
                  audienceScope: 'departments',
                  specificUserEmails: [],
                  manualSpecificUserEmails: '',
                  salesStages: [],
                  integrations: [],
                  whatItDoes: '',
                  goodFitScenarios: '',
                  notGoodFitScenarios: '',
                  previewUrl: '',
                  socialLinks: '',
                  requireAccessRequest: false,
                  botIntegration: emptyBotIntegration(),
                });
                setShowToolForm(true);
              }}
            >
              <Plus size={16} />
              Add Tool
            </button>
          </div>
        )}
        {activeTab === 'users' && !domainAdmin && (
          <button
            className="garage-btn garage-btn-primary"
            onClick={() => {
              setEditingUser(null);
              setUserFormData({
                userEmail: '',
                displayName: '',
                role: 'user',
                onboardingStatus: 'completed',
                department: '',
                workdayBusinessUnit: '',
                accessPersona: '',
                accessPersonas: [],
                grantedToolIds: [],
                canViewAdminActivity: false,
              });
              setShowUserForm(true);
            }}
          >
            <Plus size={16} />
            Add User
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="garage-admin-tabs" data-tour="admin-tabs">
        <button
          className={`garage-admin-tab ${activeTab === 'tools' ? 'active' : ''}`}
          onClick={() => handleTabChange('tools')}
        >
          <Package size={16} />
          Tools & Agents
        </button>
        {mayManageUsers && (
          <button
            className={`garage-admin-tab ${activeTab === 'users' ? 'active' : ''}`}
            data-tour="admin-users-tab"
            onClick={() => handleTabChange('users')}
            title="Assign access roles to existing users"
          >
            <Users size={16} />
            Users & Admins
          </button>
        )}
        <button
          className={`garage-admin-tab ${activeTab === 'config' ? 'active' : ''}`}
          onClick={() => handleTabChange('config')}
        >
          <Settings size={16} />
          Configuration
        </button>
        <button
          className={`garage-admin-tab ${activeTab === 'roi' ? 'active' : ''}`}
          onClick={() => handleTabChange('roi')}
          title={domainAdmin ? `Configure Time Saved / ROI (${adminPersonaScopes.join(', ')} apps & gems only)` : 'Configure Time Saved / ROI'}
        >
          <Sparkles size={16} />
          ROI
        </button>
        <button
          className={`garage-admin-tab ${activeTab === 'feedbacks' ? 'active' : ''}`}
          onClick={() => handleTabChange('feedbacks')}
          title="View user product feedback"
        >
          <MessageSquarePlus size={16} />
          Feedbacks
        </button>
        {departmentsTabEnabled && mayManageUsers && (
          <button
            className={`garage-admin-tab ${activeTab === 'departments' ? 'active' : ''}`}
            onClick={() => handleTabChange('departments')}
            title="Manage department scope and persona mappings"
          >
            <Building2 size={16} />
            Departments
          </button>
        )}
        {canOpenActivity && (
          <button
            className={`garage-admin-tab ${activeTab === 'activity' ? 'active' : ''}`}
            onClick={() => handleTabChange('activity')}
            title="Admin changes to tools, configuration, and access"
          >
            <ScrollText size={16} />
            Activity
          </button>
        )}
      </div>

      {/* Configuration Management */}
      {activeTab === 'config' && (
        <GarageConfigManagement
          userRole={userRole}
          accessPersona={accessPersona}
          accessPersonas={accessPersonas}
          onConfigChange={() => {
            // Reload local dynamic options
            loadDynamicOptions();
            // Notify parent to increment config version for other components
            if (onConfigChange) {
              onConfigChange();
            }
          }}
        />
      )}

      {/* ROI Configuration */}
      {activeTab === 'roi' && (
        <GarageRoiConfigTab
          userRole={userRole}
          accessPersona={accessPersona}
          accessPersonas={accessPersonas}
          initialToolId={initialRoiToolId}
        />
      )}

      {/* Product feedback inbox */}
      {activeTab === 'feedbacks' && <GarageFeedbacksAdminPanel />}
      {canOpenActivity && activeTab === 'activity' && <GarageAdminActivityPanel />}

      {/* Department governance */}
      {departmentsTabEnabled && mayManageUsers && activeTab === 'departments' && (
        <GarageDepartmentsAdminPanel
          userRole={userRole}
          users={users}
          onChanged={() => {
            loadDynamicOptions();
            loadUsers();
            onConfigChange?.();
          }}
        />
      )}

      {/* Tool Form */}
      {activeTab === 'tools' && showToolForm && (
        <div className="garage-admin-form-card">
          <h3>{editingTool ? 'Edit Tool' : 'Create New Tool'}</h3>
          <form onSubmit={handleCreateOrUpdateTool}>
            <div className="garage-form-grid">
              <div className="garage-form-group">
                <label>Title *</label>
                <input
                  type="text"
                  required
                  value={toolFormData.title}
                  onChange={(e) => setToolFormData({ ...toolFormData, title: e.target.value })}
                  placeholder="Tool name"
                />
              </div>
              <div className="garage-form-group">
                <label>Tool Type *</label>
                <select
                  required
                  value={toolFormData.toolType}
                  onChange={(e) => {
                    const nextType = e.target.value;
                    setToolFormData({
                      ...toolFormData,
                      toolType: nextType,
                      appSubtype: nextType === 'App' ? (toolFormData.appSubtype || 'web') : 'web',
                      rcAppUrl: nextType === 'Gem' ? '' : toolFormData.rcAppUrl,
                      steps: nextType === 'Gem' ? '' : toolFormData.steps,
                    });
                  }}
                >
                  <option value="App">App</option>
                  <option value="Gem">Gem</option>
                </select>
              </div>
              {toolFormData.toolType === 'App' && (
                <div className="garage-form-group">
                  <label>App Subtype *</label>
                  <select
                    required
                    value={toolFormData.appSubtype || 'web'}
                    onChange={(e) => setToolFormData({ ...toolFormData, appSubtype: e.target.value })}
                  >
                    <option value="web">Web</option>
                    <option value="bot">Bot</option>
                  </select>
                </div>
              )}
              {toolFormData.toolType === 'Gem' ? (
                <div className="garage-form-group">
                  <label>URL *</label>
                  <input
                    type="url"
                    required
                    value={toolFormData.url}
                    onChange={(e) => setToolFormData({ ...toolFormData, url: e.target.value })}
                    placeholder="https://..."
                  />
                </div>
              ) : toolFormData.appSubtype === 'bot' ? (
                <>
                  <div className="garage-form-group">
                    <label>Web URL</label>
                    <input
                      type="url"
                      value={toolFormData.url}
                      onChange={(e) => setToolFormData({ ...toolFormData, url: e.target.value })}
                      placeholder="https://... (optional)"
                    />
                  </div>
                  <div className="garage-form-group">
                    <label>RC App URL</label>
                    <input
                      type="url"
                      value={toolFormData.rcAppUrl}
                      onChange={(e) => setToolFormData({ ...toolFormData, rcAppUrl: e.target.value })}
                      placeholder="https://... (optional)"
                    />
                  </div>
                  <div className="garage-form-group garage-form-group-full">
                    <label>Steps</label>
                    <textarea
                      value={toolFormData.steps}
                      onChange={(e) => setToolFormData({ ...toolFormData, steps: e.target.value })}
                      placeholder="One step per line (optional)"
                      rows={4}
                    />
                  </div>
                </>
              ) : (
                <div className="garage-form-group">
                  <label>URL *</label>
                  <input
                    type="url"
                    required
                    value={toolFormData.url}
                    onChange={(e) => setToolFormData({ ...toolFormData, url: e.target.value })}
                    placeholder="https://..."
                  />
                </div>
              )}
              <div className="garage-form-group">
                <label>Status *</label>
                <select
                  required
                  value={toolFormData.status}
                  onChange={(e) => setToolFormData({ ...toolFormData, status: e.target.value })}
                >
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                </select>
              </div>
              <div className="garage-form-group">
                <label>Primary Persona/Team *</label>
                <select
                  required
                  value={toolFormData.persona}
                  onChange={(e) => setToolFormData({ ...toolFormData, persona: e.target.value })}
                  disabled={domainAdmin}
                >
                  <option value="">Select persona...</option>
                  {/* Keep saved value visible even if it is not in the current config list */}
                  {toolFormData.persona &&
                    !dynamicOptions.personas.some(
                      (p) => String(p).trim().toLowerCase() === String(toolFormData.persona).trim().toLowerCase()
                    ) && (
                      <option value={toolFormData.persona}>{toolFormData.persona}</option>
                    )}
                  {(domainAdmin ? adminPersonaScopes : dynamicOptions.personas).filter(Boolean).map(persona => (
                    <option key={persona} value={persona}>{persona}</option>
                  ))}
                </select>
                <small style={{ display: 'block', marginTop: '4px', color: 'var(--garage-text-3)', fontSize: '13px' }}>
                  This will appear on the tool card badge (e.g., "SALES · APP")
                </small>
              </div>
            </div>
            {config.BOT_INTEGRATIONS_ENABLED
              && userRole === 'admin'
              && toolFormData.toolType === 'App'
              && toolFormData.appSubtype === 'bot' && (
                <BotIntegrationForm
                  value={toolFormData.botIntegration || emptyBotIntegration()}
                  onChange={(botIntegration) => setToolFormData({ ...toolFormData, botIntegration })}
                  onFetch={handleFetchBotCredential}
                  fetching={isFetchingBotCredential}
                  onTest={handleTestBotIntegration}
                  canTest={Boolean(
                    (toolFormData.botIntegration?.botExtensionId || '').trim()
                    || (toolFormData.botIntegration?.botEmail || '').trim()
                  )}
                  testing={isTestingBot}
                />
              )}
            {config.ACCESS_REQUESTS_ENABLED && (
              <div className="garage-form-grid">
                <div className="garage-form-group">
                  <label htmlFor="garage-access-control">Access control</label>
                  <select
                    id="garage-access-control"
                    value={toolFormData.requireAccessRequest ? 'require' : 'open'}
                    onChange={(e) => setToolFormData({
                      ...toolFormData,
                      requireAccessRequest: e.target.value === 'require',
                    })}
                  >
                    <option value="open">Open launch</option>
                    <option value="require">Require access request</option>
                  </select>
                  <small style={{ display: 'block', marginTop: '4px', color: 'var(--garage-text-3)', fontSize: '13px' }}>
                    Require access request: Discover shows Request access until an admin provisions the user.
                  </small>
                </div>
              </div>
            )}
            <div className="garage-form-group">
              <label>Description *</label>
              <textarea
                rows="3"
                required
                value={toolFormData.description}
                onChange={(e) => setToolFormData({ ...toolFormData, description: e.target.value })}
                placeholder="Brief description of the tool..."
              />
            </div>
            <div className="garage-form-grid">
              <ImageUrlOrUpload
                value={toolFormData.imageUrl}
                onChange={(url) => setToolFormData({ ...toolFormData, imageUrl: url })}
              />
              <div className="garage-form-group">
                <label>Maker Emails (optional)</label>
                <input
                  type="text"
                  value={toolFormData.makerEmail}
                  onChange={(e) => setToolFormData({ ...toolFormData, makerEmail: e.target.value })}
                  placeholder="email1@ringcentral.com, email2@ringcentral.com"
                />
                <small style={{ display: 'block', marginTop: '4px', color: 'var(--garage-text-3)', fontSize: '13px' }}>
                  Optional. Leave blank to use your admin email as the maker. First email is primary; you can edit later.
                </small>
              </div>
            </div>
            <MultiSelectDropdown
              label="Tags"
              options={dynamicOptions.tags}
              value={toolFormData.tags}
              onChange={(value) => setToolFormData({ ...toolFormData, tags: value })}
              placeholder="Select tags..."
              allowCustom={true}
              helpText="Select from common tags or add custom ones"
            />

            <MultiSelectDropdown
              label="Target Roles"
              options={dynamicOptions.toolTargetRoles}
              value={toolFormData.roles}
              onChange={(value) => setToolFormData({ ...toolFormData, roles: value })}
              placeholder="Select target roles..."
              allowCustom={false}
              helpText="Job roles that will benefit from this tool (used in Matrix View)"
            />

            <MultiSelectDropdown
              label="Customer Journeys"
              options={dynamicOptions.journeys}
              value={toolFormData.journeys}
              onChange={(value) => setToolFormData({ ...toolFormData, journeys: value })}
              placeholder="Select journey stages..."
              allowCustom={false}
              helpText="Customer journey stages where this tool is useful (used in Matrix View)"
            />

            <MultiSelectDropdown
              label="Capabilities"
              options={dynamicOptions.capabilities}
              value={toolFormData.capabilities}
              onChange={(value) => setToolFormData({ ...toolFormData, capabilities: value })}
              placeholder="Select capabilities..."
              allowCustom={false}
              helpText="What this tool can do - Research, Generate, Analyze, etc. (used in filters and Matrix View)"
            />

            {/* Enhanced Metadata Section */}
            <div style={{ marginTop: '32px', paddingTop: '24px', borderTop: '2px solid var(--garage-border-2)' }}>
              <h4 style={{ marginBottom: '16px', fontSize: '16px', fontWeight: '600', color: 'var(--garage-text)' }}>
                Enhanced Tool Detail Metadata (Optional)
              </h4>

              <div className="garage-form-grid">
                <div className="garage-form-group">
                  <label>Environment</label>
                  <select
                    value={toolFormData.environment}
                    onChange={(e) => setToolFormData({ ...toolFormData, environment: e.target.value })}
                  >
                    <option value="">Select environment...</option>
                    {dynamicOptions.environments.map(env => (
                      <option key={env} value={env}>{env}</option>
                    ))}
                  </select>
                </div>
                <div className="garage-form-group">
                  <label>Model</label>
                  <select
                    value={toolFormData.model}
                    onChange={(e) => setToolFormData({ ...toolFormData, model: e.target.value })}
                  >
                    <option value="">Select model...</option>
                    {dynamicOptions.models.map(model => (
                      <option key={model} value={model}>{model}</option>
                    ))}
                  </select>
                </div>
              </div>

              <MultiSelectDropdown
                label="Personas"
                options={dynamicOptions.personas}
                value={toolFormData.personas}
                onChange={(value) => setToolFormData({ ...toolFormData, personas: value })}
                placeholder="Select personas..."
                allowCustom={false}
                helpText="Target personas for this tool (used in Matrix View)"
              />

              <MultiSelectDropdown
                label="Departments"
                options={dynamicOptions.departments}
                value={toolFormData.departments}
                onChange={(value) => {
                  if (toolFormData.audienceScope === 'specific_users') {
                    loadAudienceUsers(value);
                  }
                  setToolFormData((previous) => ({
                    ...previous,
                    departments: value,
                    specificUserEmails: [],
                    manualSpecificUserEmails: '',
                  }));
                }}
                placeholder="Select one or more departments..."
                allowCustom={false}
                helpText="Choose the departments this app is intended for"
              />

              {userRole === 'admin' && toolFormData.toolType === 'App' && (
                <div className="garage-audience-scope">
                  <div className="garage-audience-scope-header">
                    <strong>Audience access</strong>
                    <span>Choose department-wide access or restrict the app to named users.</span>
                  </div>
                  <div className="garage-audience-scope-options" role="radiogroup" aria-label="Audience access">
                    <label className={`garage-audience-scope-option ${toolFormData.audienceScope === 'departments' ? 'selected' : ''}`}>
                      <input
                        type="radio"
                        name="audienceScope"
                        value="departments"
                        checked={toolFormData.audienceScope === 'departments'}
                        onChange={() => setToolFormData((previous) => ({
                          ...previous,
                          audienceScope: 'departments',
                          specificUserEmails: [],
                          manualSpecificUserEmails: '',
                        }))}
                      />
                      <span>
                        <strong>All department users</strong>
                        <small>Everyone whose Workday Business Unit matches a selected department.</small>
                      </span>
                    </label>
                    <label className={`garage-audience-scope-option ${toolFormData.audienceScope === 'specific_users' ? 'selected' : ''}`}>
                      <input
                        type="radio"
                        name="audienceScope"
                        value="specific_users"
                        checked={toolFormData.audienceScope === 'specific_users'}
                        onChange={() => {
                          loadAudienceUsers(toolFormData.departments);
                          setToolFormData((previous) => ({
                            ...previous,
                            audienceScope: 'specific_users',
                          }));
                        }}
                      />
                      <span>
                        <strong>Specific users</strong>
                        <small>Select active employees from Workday or paste known AI Garage user emails.</small>
                      </span>
                    </label>
                  </div>

                  {toolFormData.audienceScope === 'specific_users' && (
                    <div className="garage-audience-users">
                      <MultiSelectDropdown
                        label="Eligible users"
                        options={audienceUsers.map((profile) => ({
                          value: profile.email,
                          label: profile.employeeName
                            ? `${profile.employeeName} — ${profile.email}`
                            : profile.email,
                          description: [
                            profile.businessUnit,
                            profile.businessTitle,
                            profile.location || profile.country,
                            profile.costCenterName ? `Cost center: ${profile.costCenterName}` : null,
                            profile.managerName ? `Manager: ${profile.managerName}` : null,
                          ].filter(Boolean).join(' · '),
                        }))}
                        value={toolFormData.specificUserEmails}
                        onChange={(value) => setToolFormData((previous) => ({
                          ...previous,
                          specificUserEmails: value,
                        }))}
                        placeholder={audienceUsersLoading
                          ? 'Loading users...'
                          : (toolFormData.departments.length > 0
                            ? 'Search by name, email, or department...'
                            : 'Select departments to use the directory picker...')}
                        allowCustom={false}
                        required
                        helpText={audienceUsersLoading
                          ? 'Loading active employees from Workday...'
                          : (toolFormData.departments.length === 0
                            ? 'No departments selected. You can use the email fallback below.'
                            : 'Only selected users will see this app.')}
                      />
                      <div className="garage-audience-manual">
                        <div className="garage-audience-manual-heading">
                          <strong>Paste user emails</strong>
                          <span>Fallback for environments without department mapping</span>
                        </div>
                        <div className="garage-audience-manual-row">
                          <textarea
                            rows="2"
                            value={toolFormData.manualSpecificUserEmails}
                            onChange={(event) => setToolFormData((previous) => ({
                              ...previous,
                              manualSpecificUserEmails: event.target.value,
                            }))}
                            placeholder="name.one@ringcentral.com, name.two@ringcentral.com"
                            aria-label="Paste specific user emails"
                          />
                          <button
                            type="button"
                            className="garage-btn garage-btn-secondary garage-audience-add-btn"
                            onClick={addManualAudienceEmails}
                            disabled={!toolFormData.manualSpecificUserEmails.trim()}
                          >
                            Add emails
                          </button>
                        </div>
                        <small>
                          Separate emails with commas, spaces, semicolons, or new lines. With departments selected, emails must match active Workday employees in those Business Units.
                        </small>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <MultiSelectDropdown
                label="Sales Stages"
                options={dynamicOptions.salesStages}
                value={toolFormData.salesStages}
                onChange={(value) => setToolFormData({ ...toolFormData, salesStages: value })}
                placeholder="Select sales stages..."
                allowCustom={false}
                helpText="Sales stages where this tool applies"
              />

              <MultiSelectDropdown
                label="Integrations"
                options={dynamicOptions.integrations}
                value={toolFormData.integrations}
                onChange={(value) => setToolFormData({ ...toolFormData, integrations: value })}
                placeholder="Select integrations..."
                allowCustom={true}
                helpText="Systems that this tool integrates with"
              />

              <div className="garage-form-group">
                <label>What It Does (one per line)</label>
                <textarea
                  rows="4"
                  value={toolFormData.whatItDoes}
                  onChange={(e) => setToolFormData({ ...toolFormData, whatItDoes: e.target.value })}
                  placeholder="List key features and capabilities, one per line..."
                />
              </div>

              <div className="garage-form-group">
                <label>Good Fit Scenarios (one per line)</label>
                <textarea
                  rows="3"
                  value={toolFormData.goodFitScenarios}
                  onChange={(e) => setToolFormData({ ...toolFormData, goodFitScenarios: e.target.value })}
                  placeholder="When this tool is a good fit, one scenario per line..."
                />
              </div>

              <div className="garage-form-group">
                <label>Not Good Fit Scenarios (one per line)</label>
                <textarea
                  rows="3"
                  value={toolFormData.notGoodFitScenarios}
                  onChange={(e) => setToolFormData({ ...toolFormData, notGoodFitScenarios: e.target.value })}
                  placeholder="When this tool is NOT a good fit, one scenario per line..."
                />
              </div>

              <div className="garage-form-group">
                <label>Preview URL</label>
                <input
                  type="url"
                  value={toolFormData.previewUrl}
                  onChange={(e) => setToolFormData({ ...toolFormData, previewUrl: e.target.value })}
                  placeholder="https://..."
                />
              </div>

              <div className="garage-form-group">
                <label>Social Links (JSON format)</label>
                <textarea
                  rows="2"
                  value={toolFormData.socialLinks}
                  onChange={(e) => setToolFormData({ ...toolFormData, socialLinks: e.target.value })}
                  placeholder='{"twitter": "https://twitter.com/ringcentral", "linkedin": "https://linkedin.com/company/ringcentral"}'
                />
              </div>
            </div>

            {/* Session Tracking Configuration Section (Apps Only) */}
            {/* Debug: toolFormData.toolType = {toolFormData.toolType} */}
            {toolFormData.toolType === 'App' && (
              <div style={{
                marginTop: '32px',
                paddingTop: '24px',
                borderTop: '2px solid var(--garage-border-2)'
              }}>
                <h4 style={{
                  marginBottom: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  color: 'var(--garage-text)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <span>📊</span>
                  Session Tracking Configuration
                  <span style={{
                    fontSize: '11px',
                    fontWeight: '600',
                    color: 'var(--garage-blue)',
                    background: 'var(--garage-blue-soft)',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    marginLeft: '8px'
                  }}>OPTIONAL</span>
                </h4>
                <p style={{
                  fontSize: '13px',
                  color: 'var(--garage-text-2)',
                  marginBottom: '20px',
                  lineHeight: '1.5'
                }}>
                  Configure how to track usage sessions for this app. If left blank, only Garage launches will be tracked.
                  For apps with their own session tables, provide the table name and column mappings below.
                </p>

                <div className="garage-form-group">
                  <label>
                    Source Table Name
                    <small style={{ fontWeight: 'normal', color: 'var(--garage-text-3)', marginLeft: '8px' }}>
                      (PostgreSQL table containing app sessions - unique per tool)
                    </small>
                  </label>
                  <input
                    type="text"
                    value={sessionConfig.sourceTableName}
                    onChange={(e) => setSessionConfig({
                      ...sessionConfig,
                      sourceTableName: e.target.value
                    })}
                    placeholder="e.g., my_app_sessions or myschema.my_app_sessions"
                  />
                  <small style={{ display: 'block', marginTop: '4px', color: 'var(--garage-text-3)' }}>
                    💡 <strong>Important:</strong> Each tool must have its own unique session table. You can use:
                    <br />
                    • <code style={{ background: 'var(--garage-bg-2)', padding: '2px 6px', borderRadius: '3px', fontSize: '12px' }}>table_name</code> - uses default "public" schema
                    <br />
                    • <code style={{ background: 'var(--garage-bg-2)', padding: '2px 6px', borderRadius: '3px', fontSize: '12px' }}>schema.table_name</code> - for custom schemas
                  </small>
                </div>

                <div className="garage-form-grid">
                  <div className="garage-form-group">
                    <label>Session ID Column</label>
                    <input
                      type="text"
                      value={sessionConfig.sessionIdColumn}
                      onChange={(e) => setSessionConfig({
                        ...sessionConfig,
                        sessionIdColumn: e.target.value
                      })}
                      placeholder="e.g., id"
                    />
                    <small style={{ display: 'block', marginTop: '4px', color: 'var(--garage-text-3)' }}>
                      Unique identifier for each session
                    </small>
                  </div>
                  <div className="garage-form-group">
                    <label>User Email Column</label>
                    <input
                      type="text"
                      value={sessionConfig.userEmailColumn}
                      onChange={(e) => setSessionConfig({
                        ...sessionConfig,
                        userEmailColumn: e.target.value
                      })}
                      placeholder="e.g., user_email"
                    />
                    <small style={{ display: 'block', marginTop: '4px', color: 'var(--garage-text-3)' }}>
                      Column containing user email addresses
                    </small>
                  </div>
                </div>

                <div className="garage-form-grid">
                  <div className="garage-form-group">
                    <label>Session Start Column</label>
                    <input
                      type="text"
                      value={sessionConfig.sessionStartColumn}
                      onChange={(e) => setSessionConfig({
                        ...sessionConfig,
                        sessionStartColumn: e.target.value
                      })}
                      placeholder="e.g., created_at"
                    />
                    <small style={{ display: 'block', marginTop: '4px', color: 'var(--garage-text-3)' }}>
                      Timestamp when session started
                    </small>
                  </div>
                  <div className="garage-form-group">
                    <label>
                      Session End Column
                      <span style={{
                        fontSize: '11px',
                        color: 'var(--garage-text-3)',
                        marginLeft: '4px'
                      }}>
                        (optional)
                      </span>
                    </label>
                    <input
                      type="text"
                      value={sessionConfig.sessionEndColumn}
                      onChange={(e) => setSessionConfig({
                        ...sessionConfig,
                        sessionEndColumn: e.target.value
                      })}
                      placeholder="e.g., ended_at"
                    />
                    <small style={{ display: 'block', marginTop: '4px', color: 'var(--garage-text-3)' }}>
                      Timestamp when session ended (if tracked)
                    </small>
                  </div>
                </div>

                <div className="garage-form-group">
                  <label>Incremental Sync Column</label>
                  <select
                    value={sessionConfig.incrementalColumn}
                    onChange={(e) => setSessionConfig({
                      ...sessionConfig,
                      incrementalColumn: e.target.value
                    })}
                  >
                    <option value="">-- Select a column --</option>
                    {sessionConfig.sessionIdColumn && (
                      <option value={sessionConfig.sessionIdColumn}>
                        {sessionConfig.sessionIdColumn} (session ID)
                      </option>
                    )}
                    {sessionConfig.sessionStartColumn && (
                      <option value={sessionConfig.sessionStartColumn}>
                        {sessionConfig.sessionStartColumn} (start timestamp - recommended)
                      </option>
                    )}
                    {sessionConfig.sessionEndColumn && (
                      <option value={sessionConfig.sessionEndColumn}>
                        {sessionConfig.sessionEndColumn} (end timestamp)
                      </option>
                    )}
                    {sessionConfig.userEmailColumn && (
                      <option value={sessionConfig.userEmailColumn}>
                        {sessionConfig.userEmailColumn} (user email)
                      </option>
                    )}
                  </select>
                  <small style={{ display: 'block', marginTop: '4px', color: 'var(--garage-text-3)' }}>
                    Column used to track which records have been synced. ETL will only pull new records after the last synced value.
                  </small>
                </div>

                <div className="garage-form-group" style={{ marginTop: '16px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={sessionConfig.isActive}
                      onChange={(e) => setSessionConfig({
                        ...sessionConfig,
                        isActive: e.target.checked
                      })}
                      style={{ marginRight: '8px' }}
                    />
                    <span style={{ fontWeight: '500' }}>Enable session tracking for this app</span>
                  </label>
                  <small style={{ display: 'block', marginTop: '6px', marginLeft: '24px', color: 'var(--garage-text-3)' }}>
                    When enabled, an ETL job will periodically sync sessions from your table to the unified dashboard.
                  </small>
                </div>

                {sessionConfig.sourceTableName && (
                  <div style={{ marginTop: '16px' }}>
                    <button
                      type="button"
                      className="garage-btn garage-btn-secondary"
                      onClick={handleTestConnection}
                      disabled={testingConnection}
                      style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                    >
                      <span>🔌</span>
                      {testingConnection ? 'Testing Connection...' : 'Test Connection & Preview Data'}
                    </button>
                    {connectionTestResult && (
                      <div style={{
                        marginTop: '12px',
                        padding: '12px',
                        borderRadius: '6px',
                        background: connectionTestResult.success ? 'var(--garage-green-soft)' : 'var(--garage-red-soft)',
                        border: `1px solid ${connectionTestResult.success ? 'var(--garage-green)' : 'var(--garage-red)'}`,
                        fontSize: '13px',
                        color: 'var(--garage-text)'
                      }}>
                        <strong>{connectionTestResult.success ? '✅ Success' : '❌ Failed'}</strong>
                        <p style={{ margin: '4px 0 0 0' }}>{connectionTestResult.message}</p>
                        {connectionTestResult.rowCount !== undefined && (
                          <p style={{ margin: '4px 0 0 0' }}>Found {connectionTestResult.rowCount} recent sessions</p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="garage-form-actions">
              <button
                type="button"
                className="garage-btn garage-btn-secondary"
                onClick={() => {
                  setShowToolForm(false);
                  setEditingTool(null);
                }}
              >
                Cancel
              </button>
              <button type="submit" className="garage-btn garage-btn-primary" disabled={isSaving}>
                {isSaving ? 'Saving…' : (editingTool ? 'Update Tool' : 'Create Tool')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Search and Pagination Controls - tools/users only (feedbacks has its own) */}
      {activeTab !== 'config' && activeTab !== 'roi' && activeTab !== 'feedbacks' && activeTab !== 'departments' && activeTab !== 'activity' && !loading && (
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px',
          padding: '16px',
          background: 'var(--garage-surface)',
          border: '1px solid var(--garage-border)',
          borderRadius: '12px'
        }}>
          {/* Search Bar */}
          <div className="garage-admin-search-row">
            <label className="garage-admin-search-field">
              <Search size={16} aria-hidden="true" />
              <input
                type="text"
                placeholder={
                  activeTab === 'users'
                    ? 'Search users by name or email…'
                    : `Search ${activeTab}...`
                }
                aria-label={
                  activeTab === 'users'
                    ? 'Search users by name or email'
                    : `Search ${activeTab}`
                }
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
              />
            </label>
            {activeTab === 'tools' && (
              <select
                value={toolPersonaFilter}
                onChange={(e) => {
                  setToolPersonaFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="garage-admin-filter-select"
                title="Filter tools and agents by persona"
                aria-label="Filter tools and agents by persona"
              >
                <option value="">All personas</option>
                {(dynamicOptions.personas || []).map((persona) => (
                  <option key={persona.value || persona} value={persona.value || persona}>
                    {persona.label || persona}
                  </option>
                ))}
              </select>
            )}
            {activeTab === 'users' && (
              <select
                value={personaFilter}
                onChange={(e) => {
                  setPersonaFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="garage-admin-filter-select"
                title="Filter users by persona"
              >
                <option value="">All personas</option>
                {(dynamicOptions.personas || []).map((persona) => (
                  <option
                    key={persona.value || persona}
                    value={persona.value || persona}
                  >
                    {persona.label || persona}
                  </option>
                ))}
              </select>
            )}
            {activeTab === 'users' && (
              <select
                value={userRoleFilter}
                onChange={(e) => {
                  setUserRoleFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="garage-admin-filter-select"
                title="Filter users by access role"
              >
                <option value="">All access roles</option>
                {ACCESS_ROLE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            )}
            {activeTab === 'users' && (
              <details className="garage-column-picker">
                <summary>
                  <Eye size={16} aria-hidden="true" />
                  Columns
                </summary>
                <div className="garage-column-picker-menu">
                  <div className="garage-column-picker-heading">
                    <strong>Show columns</strong>
                    <span>User and Actions are always shown</span>
                  </div>
                  {USER_COLUMN_OPTIONS.map((column) => (
                    <label key={column.key}>
                      <input
                        type="checkbox"
                        checked={isUserColumnVisible(column.key)}
                        onChange={() => toggleUserColumn(column.key)}
                      />
                      <span>{column.label}</span>
                    </label>
                  ))}
                  <button
                    type="button"
                    onClick={() => setVisibleUserColumns(DEFAULT_USER_COLUMNS)}
                  >
                    Reset to default
                  </button>
                </div>
              </details>
            )}
          </div>

          {/* Items per page and Results info */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <span style={{ fontSize: '14px', color: 'var(--garage-text-2)' }}>
              {getFilteredItems().length} results
            </span>
            <select
              value={itemsPerPage}
              onChange={(e) => handleItemsPerPageChange(e.target.value)}
              style={{
                padding: '8px 12px',
                border: '1.5px solid var(--garage-border-2)',
                borderRadius: '8px',
                background: 'var(--garage-bg)',
                color: 'var(--garage-text)',
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              <option value={10}>10 per page</option>
              <option value={25}>25 per page</option>
              <option value={50}>50 per page</option>
              <option value={100}>100 per page</option>
            </select>
          </div>
        </div>
      )}

      {activeTab === 'users' && !loading && (
        <div className="garage-user-access-summary">
          <div>
            <span>Existing users</span>
            <strong>{users.length}</strong>
          </div>
          <div>
            <span>Full Admins</span>
            <strong>{users.filter((user) => user.role === 'admin').length}</strong>
          </div>
          <div>
            <span>Persona Admins</span>
            <strong>{users.filter((user) => user.role === 'persona_admin').length}</strong>
          </div>
          <div>
            <span>Analysts & Reviewers</span>
            <strong>{users.filter((user) => user.role === 'analyst_reviewer').length}</strong>
          </div>
        </div>
      )}

      {/* Content */}
      {['tools', 'users'].includes(activeTab) && loading ? (
        <GarageLoadingScreen message="Loading..." />
      ) : (
        <>
          {/* Tools Tab */}
          {activeTab === 'tools' && (
            <div className="garage-admin-table">
              <table>
                <thead>
                  <tr>
                    <th>Tool Name</th>
                    <th>Type</th>
                    <th>Environment</th>
                    <th>Maker</th>
                    <th>Persona</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {getPaginatedItems().map((tool) => (
                    <tr key={tool.id}>
                      <td>
                        <div className="garage-admin-tool-name">{tool.title}</div>
                      </td>
                      <td>
                        <span className={`garage-tag garage-tag-${tool.toolType === 'App' ? 'blue' : 'orange'}`}>
                          {tool.toolType}
                        </span>
                      </td>
                      <td>
                        <span className={`garage-tag garage-tag-${
                          tool.environment === 'PRD' ? 'green' :
                          tool.environment === 'TEST' ? 'blue' :
                          tool.environment === 'DEV' ? 'purple' :
                          tool.environment === 'STAGING' ? 'yellow' : 'gray'
                        }`}>
                          {tool.environment || 'N/A'}
                        </span>
                      </td>
                      <td>{tool.makerName || tool.makerEmail || tool.createdBy || 'Unknown'}</td>
                      <td>
                        {tool.persona ? (
                          <span className="garage-tag garage-tag-purple">{tool.persona}</span>
                        ) : (
                          <span style={{ color: 'var(--garage-text-4)' }}>—</span>
                        )}
                      </td>
                      <td>
                        <span className={`garage-status-badge garage-status-${tool.status}`}>
                          {tool.status}
                        </span>
                      </td>
                      <td>
                        <div className="garage-admin-actions" data-tour="admin-tool-actions">
                          <button
                            className="garage-btn garage-btn-sm garage-btn-secondary"
                            onClick={() => handleEditTool(tool)}
                            title={toolActionTitle('Edit', tool)}
                            disabled={!canEditTool(tool)}
                            style={{
                              opacity: !canEditTool(tool) ? 0.5 : 1,
                              cursor: !canEditTool(tool) ? 'not-allowed' : 'pointer'
                            }}
                          >
                            <Edit2 size={14} />
                          </button>
                          {tool.status === 'published' ? (
                            <button
                              className="garage-btn garage-btn-sm garage-btn-secondary"
                              onClick={() => handleUnpublishTool(tool.id)}
                              title={toolActionTitle('Unpublish', tool)}
                              disabled={!canEditTool(tool)}
                              style={{
                                opacity: !canEditTool(tool) ? 0.5 : 1,
                                cursor: !canEditTool(tool) ? 'not-allowed' : 'pointer'
                              }}
                            >
                              <EyeOff size={14} />
                              Unpublish
                            </button>
                          ) : (
                            <button
                              className="garage-btn garage-btn-sm garage-btn-primary"
                              onClick={() => handlePublishTool(tool.id)}
                              title={toolActionTitle('Publish', tool)}
                              disabled={!canEditTool(tool)}
                              style={{
                                opacity: !canEditTool(tool) ? 0.5 : 1,
                                cursor: !canEditTool(tool) ? 'not-allowed' : 'pointer'
                              }}
                            >
                              <Eye size={14} />
                              Publish
                            </button>
                          )}
                          <button
                            className="garage-btn garage-btn-sm garage-btn-danger"
                            onClick={() => handleDeleteTool(tool.id)}
                            title={toolActionTitle('Delete', tool)}
                            disabled={!canEditTool(tool)}
                            style={{
                              opacity: !canEditTool(tool) ? 0.5 : 1,
                              cursor: !canEditTool(tool) ? 'not-allowed' : 'pointer'
                            }}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {getFilteredItems().length === 0 && (
                <div className="garage-empty">
                  <p>No tools found</p>
                </div>
              )}
            </div>
          )}

          {/* Users Tab */}
          {activeTab === 'users' && (
            <>
              {showUserForm && (
                <div
                  className="garage-admin-drawer-backdrop"
                  role="presentation"
                  onClick={() => {
                    setShowUserForm(false);
                    setEditingUser(null);
                  }}
                >
                  <aside
                    className="garage-admin-drawer garage-user-access-drawer"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="garage-user-drawer-title"
                    onClick={(event) => event.stopPropagation()}
                  >
                    <div className="garage-admin-drawer-header">
                      <div>
                        <span className="garage-eyebrow">Access assignment</span>
                        <h2 id="garage-user-drawer-title">
                          {editingUser ? 'Edit user access' : 'Add user'}
                        </h2>
                        <p>
                          {editingUser
                            ? 'Update role and governance scope without changing the Workday identity.'
                            : 'Add an existing AI Garage user and set their initial access.'}
                        </p>
                      </div>
                      <button
                        type="button"
                        aria-label="Close user form"
                        onClick={() => {
                          setShowUserForm(false);
                          setEditingUser(null);
                        }}
                      >
                        <X size={20} />
                      </button>
                    </div>
                  <form onSubmit={handleCreateOrUpdateUser}>
                    {editingUser && (
                      <div className="garage-user-selection-summary">
                        <div className="garage-user-avatar-compact">
                          {(userFormData.displayName || userFormData.userEmail || '?').charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <strong>{userFormData.displayName || 'Unnamed user'}</strong>
                          <span>{userFormData.userEmail}</span>
                        </div>
                        <span className={`garage-status-badge garage-status-${userFormData.role}`}>
                          {ACCESS_ROLE_LABELS[userFormData.role] || userFormData.role}
                        </span>
                      </div>
                    )}
                    <div className="garage-form-grid garage-user-drawer-grid">
                      <div className="garage-form-field">
                        <label>Email *</label>
                        <input
                          type="email"
                          value={userFormData.userEmail}
                          onChange={(e) => setUserFormData({ ...userFormData, userEmail: e.target.value })}
                          required
                          disabled={editingUser}
                          placeholder="user@ringcentral.com"
                        />
                      </div>

                      <div className="garage-form-field">
                        <label>Display Name</label>
                        <input
                          type="text"
                          value={userFormData.displayName}
                          onChange={(e) => setUserFormData({ ...userFormData, displayName: e.target.value })}
                          disabled={domainAdmin}
                          placeholder="John Doe"
                        />
                      </div>

                      {userFormData.role === 'persona_admin' && (
                      <div className="garage-form-field">
                        <MultiSelectDropdown
                          label="Access Personas"
                          options={(dynamicOptions.personas || []).map((persona) => ({
                            value: persona.value || persona,
                            label: persona.label || persona,
                          }))}
                          value={domainAdmin ? adminPersonaScopes : userFormData.accessPersonas}
                          onChange={(selectedPersonas) => {
                            setUserFormData({
                              ...userFormData,
                              accessPersona: selectedPersonas[0] || '',
                              accessPersonas: selectedPersonas,
                            });
                            emitGovernanceTelemetry('admin_access_persona_selected', {
                              actorType: userRole,
                              selectedRole: userFormData.role,
                              personaId: personaTelemetryId(
                                selectedPersonas[selectedPersonas.length - 1]
                              ),
                            });
                          }}
                          placeholder="Select one or more personas..."
                          required
                          helpText="Required for Persona Admin access. New active personas from Configuration appear here automatically."
                        />
                        <small>
                          {domainAdmin
                            ? 'Your personas are fixed by your current access.'
                            : 'The Persona Admin can manage all selected personas.'}
                        </small>
                      </div>
                      )}

                      <div className="garage-form-field">
                        <label>Access Role *</label>
                        <select
                          value={userFormData.role}
                          onChange={(e) => {
                            const role = e.target.value;
                            setUserFormData({
                              ...userFormData,
                              role,
                              accessPersona: role === 'persona_admin'
                                ? userFormData.accessPersona
                                : '',
                              accessPersonas: role === 'persona_admin'
                                ? userFormData.accessPersonas
                                : [],
                              canViewAdminActivity: role === 'admin'
                                ? userFormData.canViewAdminActivity
                                : false,
                            });
                            emitGovernanceTelemetry('admin_access_role_selected', {
                              actorType: userRole,
                              previousRole: editingUser?.role || 'user',
                              selectedRole: role,
                              personaId: personaTelemetryId(
                                domainAdmin ? accessPersona : userFormData.accessPersona
                              ),
                            });
                          }}
                          required
                        >
                          {ACCESS_ROLE_OPTIONS
                            .filter(
                              (roleOption) => !domainAdmin
                                || !['admin', 'analyst_reviewer'].includes(roleOption.value)
                            )
                            .map((roleOption) => (
                            <option key={roleOption.value} value={roleOption.value}>
                              {roleOption.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      {mayManageUsers && userFormData.role === 'admin' && (
                        <div className="garage-form-field">
                          <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <input
                              type="checkbox"
                              checked={Boolean(userFormData.canViewAdminActivity)}
                              onChange={(event) => setUserFormData({
                                ...userFormData,
                                canViewAdminActivity: event.target.checked,
                              })}
                            />
                            Can view the Activity log
                          </label>
                          <small>
                            Only Full Admins can be granted this. It shows tool, configuration, and access changes.
                          </small>
                        </div>
                      )}

                      <div className="garage-form-field">
                        <label>Onboarding Status</label>
                        <select
                          value={userFormData.onboardingStatus}
                          onChange={(e) => setUserFormData({ ...userFormData, onboardingStatus: e.target.value })}
                          disabled={domainAdmin}
                        >
                          <option value="pending">Pending</option>
                          <option value="in_progress">In Progress</option>
                          <option value="completed">Completed</option>
                          <option value="rejected">Rejected</option>
                        </select>
                      </div>

                      <div className="garage-form-field">
                        <label>Workday Business Unit</label>
                        <input
                          type="text"
                          value={userFormData.workdayBusinessUnit || ''}
                          disabled
                          placeholder="Not found in Workday"
                        />
                      </div>
                    </div>

                    {!domainAdmin && (
                      <MultiSelectDropdown
                        label="Granted tools (explicit access)"
                        options={tools.map((t) => ({ value: t.id, label: t.title || t.id }))}
                        value={userFormData.grantedToolIds || []}
                        onChange={(value) => setUserFormData({ ...userFormData, grantedToolIds: value })}
                        placeholder="Grant specific tools..."
                        allowCustom={false}
                        helpText="These tools appear in Discover even without a department/persona match"
                      />
                    )}

                    <div className="garage-admin-drawer-actions">
                      <button type="submit" className="garage-btn garage-btn-primary">
                        {domainAdmin ? 'Save Role' : editingUser ? 'Update User' : 'Create User'}
                      </button>
                      <button
                        type="button"
                        className="garage-btn garage-btn-secondary"
                        onClick={() => {
                          setShowUserForm(false);
                          setEditingUser(null);
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                  </aside>
                </div>
              )}

              <div className="garage-admin-tools-table">
                <table>
                  <thead>
                    <tr>
                      {renderUserSortHeader('User', 'userEmail')}
                      {isUserColumnVisible('role')
                        && renderUserSortHeader('Access Role', 'role')}
                      {isUserColumnVisible('persona')
                        && renderUserSortHeader('Access Persona', 'accessPersona')}
                      {isUserColumnVisible('workday')
                        && <th>Workday Business Unit</th>}
                      {isUserColumnVisible('activity')
                        && renderUserSortHeader('Last Activity', 'lastLoginAt')}
                      {isUserColumnVisible('organization') && <th>Organization</th>}
                      {isUserColumnVisible('status') && <th>Active User / Status</th>}
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getPaginatedItems().map((user) => {
                      const teamLabel = resolveOnboardingOptionLabel(
                        user.persona,
                        dynamicOptions.onboardingTeams
                      );
                      const jobRoleLabel = resolveOnboardingOptionLabel(
                        user.userRole,
                        dynamicOptions.onboardingJobRoles
                      );
                      const personaLabels = (
                        user.accessPersonas
                        || (user.accessPersona ? [user.accessPersona] : [])
                      ).map((persona) => (
                        resolveOnboardingOptionLabel(
                          persona,
                          dynamicOptions.personas
                        ) || persona
                      ));
                      return (
                      <tr
                        key={user.userEmail}
                        className={[
                          'garage-user-row',
                          canEditUserAccess(user) ? 'is-selectable' : 'is-locked',
                          editingUser?.userEmail === user.userEmail ? 'garage-user-row-selected' : '',
                        ].filter(Boolean).join(' ')}
                        tabIndex={canEditUserAccess(user) ? 0 : undefined}
                        role={canEditUserAccess(user) ? 'button' : undefined}
                        aria-label={
                          canEditUserAccess(user)
                            ? `Edit access for ${user.displayName || user.userEmail}`
                            : undefined
                        }
                        title={
                          canEditUserAccess(user)
                            ? 'Click to edit access'
                            : 'You cannot modify yourself, Full Admins, or privileged users in another persona'
                        }
                        onClick={() => {
                          if (canEditUserAccess(user)) {
                            handleEditUser(user);
                            return;
                          }
                          emitGovernanceTelemetry('admin_guardrail_triggered', {
                            actorType: userRole,
                            previousRole: user.role || 'user',
                            personaId: personaTelemetryId(user.accessPersona),
                            outcome: 'blocked',
                            guardrailReason: userAccessGuardrailReason(user),
                          });
                        }}
                        onKeyDown={(event) => {
                          if (!canEditUserAccess(user)) return;
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            handleEditUser(user);
                          }
                        }}
                      >
                        <td>
                          <div className="garage-user-table-identity">
                            <span className="garage-user-avatar-compact">
                              {(user.displayName || user.userEmail || '?').charAt(0).toUpperCase()}
                            </span>
                            <span>
                              <strong>{user.displayName || 'Unnamed user'}</strong>
                              <small>{user.userEmail}</small>
                            </span>
                          </div>
                        </td>
                        {isUserColumnVisible('role') && (
                        <td>
                          <span className={`garage-status-badge garage-status-${user.role}`}>
                            {ACCESS_ROLE_LABELS[user.role] || user.role}
                          </span>
                        </td>
                        )}
                        {isUserColumnVisible('persona') && (
                        <td>
                          {personaLabels.length ? (
                            <span className="garage-tag garage-tag-purple">
                              {personaLabels.join(', ')}
                            </span>
                          ) : (
                            <span className="garage-text-muted">—</span>
                          )}
                        </td>
                        )}
                        {isUserColumnVisible('workday') && (
                        <td>
                          {user.workdayBusinessUnit ? (
                            <span>{user.workdayBusinessUnit}</span>
                          ) : (
                            <span className="garage-text-muted">Not found</span>
                          )}
                        </td>
                        )}
                        {isUserColumnVisible('activity') && (
                        <td>
                          {user.lastLoginAt
                            ? (
                              <div className="garage-user-table-stack">
                                <span>{new Date(user.lastLoginAt).toLocaleDateString()}</span>
                                <small>{user.loginCount ?? 0} login{user.loginCount === 1 ? '' : 's'}</small>
                              </div>
                            )
                            : <span className="garage-text-muted">Never signed in</span>}
                        </td>
                        )}
                        {isUserColumnVisible('organization') && (
                        <td>
                          <div className="garage-user-table-stack">
                            <span>{teamLabel || '—'}</span>
                            <small>{jobRoleLabel || 'No job role'}</small>
                          </div>
                        </td>
                        )}
                        {isUserColumnVisible('status') && (
                        <td>
                          <div className="garage-user-table-status">
                            {user.isActive ? (
                              <CheckCircle size={15} style={{ color: '#10b981' }} />
                            ) : (
                              <XCircle size={15} style={{ color: '#ef4444' }} />
                            )}
                            <span>{user.isActive ? 'Active' : 'Inactive'}</span>
                            <small>{String(user.onboardingStatus || '').replace('_', ' ')}</small>
                          </div>
                        </td>
                        )}
                        <td onClick={(event) => event.stopPropagation()}>
                          <div className="garage-admin-actions">
                            <button
                              className="garage-btn garage-btn-sm garage-btn-secondary"
                              onClick={() => handleEditUser(user)}
                              disabled={!canEditUserAccess(user)}
                              title={
                                canEditUserAccess(user)
                                  ? 'Assign access role'
                                  : 'You cannot modify yourself, Full Admins, or privileged users in another persona'
                              }
                            >
                              <Edit2 size={14} />
                            </button>
                            {!domainAdmin && (
                              <button
                                className="garage-btn garage-btn-sm garage-btn-danger"
                                onClick={() => handleDeleteUser(user)}
                                title="Delete user"
                              >
                                <Trash2 size={14} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
                {users.length === 0 && (
                  <div className="garage-empty">
                    <p>No user profiles found.</p>
                    <p>
                      User profiles control the four BRD access roles.<br />
                      Only Full Admins can add users and manage access roles.
                    </p>
                  </div>
                )}
              </div>
            </>
          )}
        </>
      )}

      {/* Pagination Controls - Show for all tabs except config */}
      {!['config', 'roi', 'feedbacks', 'departments', 'activity'].includes(activeTab) && !loading && getTotalPages() > 1 && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '12px',
          marginTop: '32px',
          padding: '20px',
          background: 'var(--garage-surface)',
          border: '1px solid var(--garage-border)',
          borderRadius: '12px'
        }}>
          {/* Previous Button */}
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            style={{
              padding: '8px 16px',
              border: '1.5px solid var(--garage-border-2)',
              borderRadius: '8px',
              background: currentPage === 1 ? 'var(--garage-surface)' : 'var(--garage-bg)',
              color: currentPage === 1 ? 'var(--garage-text-3)' : 'var(--garage-text)',
              fontSize: '14px',
              fontWeight: '600',
              cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
              opacity: currentPage === 1 ? 0.5 : 1
            }}
          >
            Previous
          </button>

          {/* Page Numbers */}
          <div style={{ display: 'flex', gap: '6px' }}>
            {Array.from({ length: getTotalPages() }, (_, i) => i + 1).map((page) => {
              // Show first, last, current, and pages around current
              const showPage = page === 1 ||
                               page === getTotalPages() ||
                               Math.abs(page - currentPage) <= 1;

              if (!showPage) {
                // Show ellipsis
                if (page === currentPage - 2 || page === currentPage + 2) {
                  return <span key={page} style={{ padding: '8px 4px', color: 'var(--garage-text-3)' }}>...</span>;
                }
                return null;
              }

              return (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  style={{
                    minWidth: '36px',
                    padding: '8px 12px',
                    border: '1.5px solid var(--garage-border-2)',
                    borderRadius: '8px',
                    background: page === currentPage ? 'var(--garage-orange)' : 'var(--garage-bg)',
                    color: page === currentPage ? '#ffffff' : 'var(--garage-text)',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  {page}
                </button>
              );
            })}
          </div>

          {/* Next Button */}
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === getTotalPages()}
            style={{
              padding: '8px 16px',
              border: '1.5px solid var(--garage-border-2)',
              borderRadius: '8px',
              background: currentPage === getTotalPages() ? 'var(--garage-surface)' : 'var(--garage-bg)',
              color: currentPage === getTotalPages() ? 'var(--garage-text-3)' : 'var(--garage-text)',
              fontSize: '14px',
              fontWeight: '600',
              cursor: currentPage === getTotalPages() ? 'not-allowed' : 'pointer',
              opacity: currentPage === getTotalPages() ? 0.5 : 1
            }}
          >
            Next
          </button>
        </div>
      )}

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        variant={confirmModal.variant}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal({ ...confirmModal, isOpen: false })}
      />

      {/* Alert Modal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        title={alertModal.title}
        message={alertModal.message}
        variant={alertModal.variant}
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
      />

      {/* Helper Tour — only after onboarding is done */}
      {showTour && !showOnboarding && (
        <TourGuide
          steps={ADMIN_TOUR_STEPS}
          storageKey="tour-completed-admin"
          onComplete={() => setShowTour(false)}
          onSkip={() => setShowTour(false)}
        />
      )}
    </div>
  );
};

export default GarageAdminView;
